/**
 * The /v1 transport contract for the core model: the template catalogue,
 * onboarding apply, and the schema read API. Stages 2 and 3 of
 * docs/plans/done/core-model.md. Transport shapes only, the same rule as auth.ts:
 * no business logic lives here.
 *
 * Extensionless relative imports, for the same reason index.ts gives: this
 * package is consumed as TypeScript source by every app, and apps/mobile does
 * not set allowImportingTsExtensions.
 */
import type { RecordCapability } from './auth';
import type { DateConditionOp } from './date-conditions';
import type { ChoiceFilter, FieldSpec, FormulaSpec, LinesFieldSpec, MustBeRule } from './formula';
import type { ComponentId } from './generated/component-ids';
import type { PurposeId } from './generated/purposes';
import type { RecordLayout } from './layout';
/** An account's type, as the ledger keeps it (L15): one list, in patterns.ts. */
import type { LedgerAccountType } from './ledger/patterns';
import type { OpenRowDefault } from './open-row';
import type { ValueTypeId } from './generated/value-types';
import type { EntryReaches } from './roles';
import type { RoutinePaused } from './routines';
import type { EntryStatusValue } from './team';
import type { WorkspaceBoardCard } from './setup-draft/cards';

/**
 * POST /v1/onboarding/apply
 *
 * Auth required, and it refuses a request that carries x-alluzo-tenant: this
 * is the one route that creates the tenant it is about, so a header naming
 * one is a confused caller. The no-AI fallback: a name, a currency, and the
 * first blank record. New businesses with a model key use compose instead.
 */
export type OnboardingApplyRequest = {
  businessName: string;
  /** Asked at creation, never inferred, per D12. ISO 4217, such as INR. */
  currency: string;
  /**
   * The first record. A business with no records is refused. The server builds
   * the same `blankSpec` the home flow builds.
   */
  blank: { name: string; counts: RecordBlankCounts };
};

/**
 * What a record is, mirroring app.record_kind. A `record` holds the entries a
 * person saves, a `directory` holds the people or things other records point
 * at, and a `lines` record holds the lines of one entry: an invoice's items, a
 * stay's rooms. The server makes a lines record beside the `lines` field that
 * points at it, and it never shows in the record list, on home or in a picker.
 */
export const RECORD_KINDS = ['record', 'directory', 'lines'] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

/**
 * The kinds a person sees and makes. Every list, picker, proposal and setup
 * draft names one of these, never `lines`: a lines record is the server's,
 * reached only through the entry that owns it.
 */
export type VisibleRecordKind = Exclude<RecordKind, 'lines'>;

/** One record onboarding created. The home screen lists these. */
export type OnboardedRecord = {
  id: string;
  name: string;
  kind: VisibleRecordKind;
  nounKey: string;
  entryNounKey: string;
};

export type OnboardingApplyResponse = {
  tenantId: string;
  records: OnboardedRecord[];
};

/**
 * Which way one record moves money, read off the `sign` purpose.
 *
 * A constant answers every row, so the record itself points one way. A field
 * answers per row, so the record points both ways and only its rows know
 * which. A record with no measure and no sign moves nothing.
 */
export type RecordDirection = 'in' | 'out' | 'mixed';

/**
 * What the CALLER may do in one record or list, sent so the device can draw a
 * screen that agrees with the server.
 *
 * A COURTESY, NEVER A FENCE. The server refuses regardless, and it is the only
 * thing that does. Every use of this on a device hides or relabels a control;
 * no screen may skip a request because the local copy said no.
 *
 * `field_policy` is deliberately absent: it is already applied to the schema
 * before it leaves, so sending it would let a device disagree with a payload
 * it has already been given. `writableFields` is NOT applied to the schema —
 * a narrowed member still sees every field — so it travels here, and the new
 * entry form leaves the fields outside the family undrawn rather than offer a
 * save the server will refuse (FIELD_NOT_WRITABLE), which offline would be a
 * 4xx and a dropped outbox row.
 */
export type RecordGrantSummary = {
  capabilities: RecordCapability[];
  /** `own_entries` means the rows of other people never arrive at all. */
  rowScope: 'all' | 'own_entries';
  /** `submit_draft` and `request` change what the save button SAYS. */
  writeMode: 'direct' | 'submit_draft' | 'request';
  balanceVisible: boolean;
  direction: 'in_only' | 'out_only' | null;
  /** The field ids this person may write. Null — or, on the wire, absent — means every field. */
  writableFields: string[] | null;
  /**
   * Whose entries each entry permission reaches: Off, Own, Their team or
   * Everyone (web-redesign-02, phase 3.2). `rowScope` above is kept for a
   * phone already in people's hands, and says `all` for Their team, because the
   * server has already cut the rows to the team: an old phone that does not
   * know `reach` never prunes a row it may see. Absent from a summary an older
   * server sent.
   */
  reach?: EntryReaches;
  /**
   * The authors Their team covers, the person first: sent only when `reach.see`
   * is Their team (web-redesign-04 phase 3). The phone keeps those people's
   * rows and prunes the rest. Absent from an older server's summary, and then
   * a phone keeps every row it holds.
   */
  team?: string[];
  /**
   * A digest of the fields hidden from this person in this record, absent when
   * none is. Only ever compared: when it moves, the phone's schema and rows for
   * the record were cut to an older mask and are read again.
   */
  maskKey?: string;
};

/**
 * What one record HOLDS, in one word, so its glyph can say so.
 *
 * Every list on the home screen wears the same grey glyph today, so Staff,
 * Locations and Payment modes look like one thing. A record knows what it
 * holds; this is the vocabulary it says it in.
 *
 * A closed list of NAMES and not of icon files, for the reason every other
 * closed list here is one: the record says `people` and the device decides
 * which icon of its own design system that draws, so the same record wears the
 * family glyph on both platforms and a new icon set is a change on one side.
 * Twenty-three words, wide enough for the lists a shop keeps and narrow enough
 * that a person picks from a screen rather than searches.
 *
 * It is stored in `app.record.prefs` under `icon`, beside `capture`.
 */
export const RECORD_ICON_IDS = [
  'people',
  'person',
  'places',
  'buildings',
  'trips',
  'things',
  'stock',
  'shopping',
  'money',
  'payments',
  'bank',
  'categories',
  'work',
  'tasks',
  'documents',
  'health',
  'tools',
  'devices',
  'power',
  'water',
  'calendar',
  'book',
  'list',
] as const;

export type RecordIconId = (typeof RECORD_ICON_IDS)[number];

export const isRecordIconId = (value: unknown): value is RecordIconId =>
  typeof value === 'string' && (RECORD_ICON_IDS as readonly string[]).includes(value);

/** One record as the home screen draws it. */
export type RecordListItem = {
  id: string;
  name: string;
  kind: VisibleRecordKind;
  nounKey: string;
  entryNounKey: string;
  currency: string | null;
  /** Null when the record measures nothing. A directory always is. */
  direction: RecordDirection | null;
  /**
   * What this record holds, as the glyph should draw it. The EFFECTIVE answer,
   * not the stored one: a record that was never given an icon is answered the
   * default here, so the catalog never has to work one out. A directory with a
   * live phone field is `people`, any other directory is `list`, and a record
   * is null and keeps the direction glyph it already draws.
   *
   * Optional, and absent means exactly what null means. `toRecordListItem` is
   * the one assembly of this row and it always answers the key, so nothing on
   * the wire is ever without it; the option is for the mirror and the fixtures
   * that shape a catalog row to test something else entirely. Read
   * `icon ?? null`, exactly as `capture` is read.
   */
  icon?: RecordIconId | null;
  /** Live rows only. A tombstoned entry is not counted. */
  entryCount: number;
  /** The `record_headline` aggregate, signed. Null when there is no measure. */
  headlineValue: number | null;
  /** When an entry was last written here, ISO 8601. Null when there are none. */
  lastEntryAt: string | null;
  /**
   * What the caller may do in this record or list. Null for an owner, who holds
   * everything without a grant row — so "owner" is one check on the device and
   * not a comparison of eight strings.
   *
   * A device that reads null must treat it as PERMISSIVE. Null is also what an
   * older payload leaves behind, and hiding a button on missing data would lock
   * a person out of their own records after an upgrade, offline.
   */
  grant: RecordGrantSummary | null;
  /**
   * The person's own word for one entry, set in Customize. Null means the
   * record still says `entryNounKey`. Read `entryNoun ?? null`: the key is
   * optional for the fixtures, and every server answer carries it.
   */
  entryNoun?: RecordEntryNoun | null;
  /**
   * Who can use this record: every primary admin, and everyone holding access
   * to it. The People filter and the avatars on Records home read it.
   *
   * GET /records answers it; the sync pull does not, so a row off the mirror
   * is without it. Absent means "not asked", never "nobody".
   */
  people?: RecordPerson[];
  /**
   * This month's money in and out, summed per entry by the same sign rule the
   * day's totals use, in `currency`. Null when this caller may not see the
   * amount (a hidden measure, or the balance withheld) or the record holds no
   * money. GET /records only, like `people`.
   */
  inThisMonth?: number | null;
  outThisMonth?: number | null;
  /**
   * An Accounts list no record picks a category from (L65): it holds the
   * accounts every money rule counts in, but nobody manages it yet, so the
   * sidebar and the Lists pages leave it out; a link to it still opens it.
   * Absent means shown.
   */
  quiet?: boolean;
  /**
   * A list only: the records that pick from it through a live field, among
   * the records this caller may open. Empty on a list nothing picks from, and
   * absent on a record. GET /records only.
   */
  usedBy?: string[];
  /**
   * Who wrote the latest live entry here, the "who" of "Last changed · who".
   * Null when there are no entries. A list only; GET /records only.
   */
  changedBy?: RecordPerson | null;
};

/** One person, as a record row names them. `name` is null when they gave none. */
export type RecordPerson = {
  userId: string;
  name: string | null;
};

/**
 * What one entry is called, in the person's own words.
 *
 * Free text the workspace owns, so it can never be a catalogue key — the same
 * rule a choice's `label` follows. Both forms are stored, because a word a
 * person typed has no plural rule we could apply: "sale" and "sales", "dues"
 * and "dues". Each is 1 to 30 letters.
 */
export type RecordEntryNoun = {
  one: string;
  other: string;
};

/**
 * Today, across every record the caller may open.
 *
 * Summed per row, not per record, because a record whose sign is a field holds
 * rows pointing both ways. One currency: records that carry another are left
 * out, and `currency` says which one these two numbers are in.
 */
export type RecordListToday = {
  currency: string | null;
  in: number;
  out: number;
};

/**
 * GET /v1/records. Tenant header required.
 *
 * `?tz=` names the caller's IANA zone, which is how the server learns what
 * calendar day "today" is: there is no tenant timezone column yet. An absent
 * or unknown zone falls back to UTC.
 */
export type RecordListResponse = {
  today: RecordListToday;
  records: RecordListItem[];
};

/**
 * POST /v1/records. Tenant header required.
 *
 * Adds one blank record or list to a business that already exists. The
 * currency is the tenant's own, never a body field: the business answered
 * that once.
 */
export type RecordCreateRequest = { blank: RecordBlankSpec };

/**
 * What each entry of a blank record counts, in the words the screen asks it in.
 *
 * This one answer settles three bindings a person must never be asked about by
 * name: `measure` names the field the totals read, `sign` says which way the
 * money points, and `focused_input` says which box opens first. It is answered
 * once, at creation, because `PATCH /structure` holds no binding vocabulary
 * and a record that changed its mind would re-read every entry it already has.
 */
export type RecordBlankCounts = 'money_in' | 'money_out' | 'money_both' | 'count' | 'none';

/**
 * The five answers, in the order a person meets them.
 *
 * Here rather than in a route, because two routes now read it — the blank
 * create and onboarding apply — and two copies of a closed list is one copy
 * that goes stale the day a sixth answer lands.
 */
export const BLANK_COUNTS: readonly RecordBlankCounts[] = [
  'money_in',
  'money_out',
  'money_both',
  'count',
  'none',
];

export const isBlankCounts = (value: unknown): value is RecordBlankCounts =>
  typeof value === 'string' && BLANK_COUNTS.includes(value as RecordBlankCounts);

/** The body of a blank create. A directory answers `kind` and `name` only. */
export type RecordBlankSpec = {
  kind: VisibleRecordKind;
  /** The tenant's own word, trimmed. 1 to 60 letters, as a field label is. */
  name: string;
  /** Required on a record, refused on a directory: a list counts nothing. */
  counts?: RecordBlankCounts;
};

/** What the apply created, in the shape home already lists. */
export type RecordCreateResponse = { records: OnboardedRecord[] };

/**
 * POST /v1/records/:recordId/duplicate. Tenant header required.
 *
 * A new record with the same fields, choices, bindings, icon and way of
 * writing entries, and none of the entries, links or access ("Fields only").
 * `name` absent takes "<name> copy", with a number when that is taken.
 * Answered with `RecordCreateResponse`, like an add.
 */
export type RecordDuplicateRequest = { name?: string };

/**
 * GET /v1/records/:recordId/removal — what deleting one record or list would
 * take with it, for the serious-step modal — and what DELETE answers once it
 * has.
 *
 * Deleting archives the record: its entries go with it and stop being read
 * anywhere, and on a list every field
 * that picks from it is taken out of its record. Entries already written in
 * those records keep what they hold.
 */
export type RecordRemovalImpact = {
  recordId: string;
  name: string;
  kind: VisibleRecordKind;
  /** Live entries that go with it. */
  entries: number;
  /** The fields in other records that pick from this list. Empty on a record. */
  pickedBy: Array<{
    recordId: string;
    recordName: string;
    fieldId: string;
    fieldLabel: string;
    /** Live entries of that record that picked something through the field. */
    entries: number;
  }>;
};

/** DELETE /v1/records/:recordId */
export type RecordDeleteResponse = { removed: RecordRemovalImpact };

/**
 * GET /v1/records/:listId/references — how much each entry of a list is used.
 *
 * Counted only where this caller may read: records they may open, through
 * fields they may see, over entries within their reach (their own, for
 * someone who sees only their own).
 */
export type ListReferencesResponse = {
  /** Each live field that picks from this list, with how many entries picked. */
  usedBy: Array<{ recordId: string; fieldId: string; fieldLabel: string; entries: number }>;
  /**
   * Keyed by list entry id; an entry nobody picked is absent. `entries` is how
   * many entries picked it ("842 entries picked it"), `records` in how many
   * records ("Used in 3 records").
   */
  entries: Record<string, { entries: number; records: number }>;
};

/** How a purpose is answered: a field, or a constant when the purpose allows one. */
export type RecordBindingAnswer = { fieldId: string } | { constNum: 1 | -1 };

/**
 * The colour one choice wears when it is chosen.
 *
 * A register is read by colour before it is read by letter: on a sheet of forty
 * names, green and red say who came in faster than any word can. Four words,
 * and they are the four the design system already speaks, so nothing here has
 * to name a colour — the device maps each to its own feedback tone.
 *
 * A choice with no tone keeps the brand tint the form's chips wear today, which
 * is what every choice on every record looks like now.
 */
export type RecordCaptureTone = 'positive' | 'notice' | 'negative' | 'information';

/**
 * The four, as a list, because both sides check a stored word against it. Same
 * reason `BLANK_COUNTS` is a list: two copies of a closed set is one copy that
 * goes stale.
 */
export const CAPTURE_TONES: readonly RecordCaptureTone[] = [
  'positive',
  'notice',
  'negative',
  'information',
];

export const isCaptureTone = (value: unknown): value is RecordCaptureTone =>
  typeof value === 'string' && CAPTURE_TONES.includes(value as RecordCaptureTone);

/**
 * How a record's entries are WRITTEN, when a form one at a time is not the
 * shape of the job.
 *
 * A staff attendance record wants one screen per day: every person listed, one
 * status each, one save. So the setting lives on the RECORD and not on a field
 * — the field still says how one value looks, and this says how a day's worth
 * of them are collected. It is stored in `app.record.prefs` under `capture`.
 *
 * One mode ships, `sheet`, and a mark it writes is an ordinary entry: same
 * head, same version, same event, same totals. A later mode is this same key
 * with another word.
 *
 *   over    a reference field. One row of the list it points at, one line.
 *   cell    a select field. The mark, one tap.
 *   shared  the fields drawn once at the top rather than per line. It holds
 *           the field bound to `time_axis` — the day the sheet is for — and
 *           may add other reference fields.
 *   quick   a choice of `cell` the one-tap shortcut fills down the sheet, or
 *           null when the record offers none.
 */
export type RecordCapture = {
  mode: 'sheet';
  over: string;
  cell: string;
  shared: string[];
  quick: string | null;
};

/**
 * One choice of a select field. Not in the plan's response sketch, and added
 * on purpose: a select is undrawable without its choices, and a sign that
 * lives per option is unreadable without them. The id is what an entry stores
 * in its reference slot.
 */
export type RecordFieldOption = {
  id: string;
  /**
   * The choice's stable key (`months`, `paid`), set on a choice a kit,
   * playbook or setup made. A formula reads a select by it: a `date_add`
   * takes its unit from the key of the choice picked (`optionKeysOf`).
   * Optional, so a schema cached before it existed still reads: then the
   * choice answers no key.
   */
  key?: string | null;
  /** Set on an option a template shipped. The catalogue holds the word. */
  labelKey: string;
  /**
   * Set on an option a person typed in the structure editor, and null on one a
   * template shipped. Free text, so it can never be a catalogue key: the
   * device's `t()` throws on a key it does not hold, by design, and a shop's
   * own word for a choice is not ours to translate. Read `label ?? t(labelKey)`.
   */
  label: string | null;
  sign: 1 | -1 | null;
  /**
   * The colour this choice wears when it is chosen. Null on nearly every one,
   * and then the choice keeps the brand tint the form has always drawn.
   *
   * Optional, and absent means exactly what null means. The server always
   * sends the key — `fieldOptions` in routes/records.ts fills it or nulls it —
   * so this is not a wire-level maybe; it is a courtesy to the many places
   * inside the server that build a choice list to answer one question about a
   * sign and have no colour to say anything about. Read `tone ?? null`.
   */
  tone?: RecordCaptureTone | null;
};

/**
 * Above this many options, a choice stops going on the form and goes behind a
 * bottom sheet instead.
 *
 * It lives here because it is now a contract between the two sides. The device
 * decides the shape of a field from it, and the server decides from the same
 * number whether to send a directory's rows with the schema at all (see
 * `targetRows`). Two copies of it would drift, and the drift would look like a
 * field that draws chips one moment and a sheet the next.
 *
 * Four is not new: `prefersSegmentedControl` has always drawn a native
 * segmented control at four or fewer and fallen back above it.
 */
export const INLINE_OPTION_LIMIT = 4;

/** One row of a directory a reference field points at. */
export type RecordTargetRow = {
  /** The entry's uuid. This is what a reference field reads and writes. */
  id: string;
  /** The row's `title` binding, already resolved from its slot. */
  title: string;
  /**
   * The row's own value of the field's `filter.field`: the parent row it
   * belongs to (the Category of a Sub-category). Null when the field has no
   * filter, or when the row names no parent.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  filterValue?: string | null;
};

export type RecordSchemaField = {
  id: string;
  /** Free text the tenant owns. Everything else human-facing here is a key. */
  label: string;
  type: ValueTypeId;
  tier: string;
  /** Walked through resolveComponent, so the device can always draw it. */
  component: ComponentId;
  props: Record<string, unknown>;
  required: boolean;
  ord: number;
  sticky: boolean;
  helpText: string | null;
  storage: string;
  targetRecordId: string | null;
  /** Null on every type but select. */
  options: RecordFieldOption[] | null;
  /**
   * The rows of `targetRecordId`, but only when there are few enough of them to
   * draw on the form. Null on every field that is not a reference.
   *
   * A reference field's options live in another record, so unlike a select it
   * cannot know how many there are without asking. Asking at mount would cost a
   * request per field and would fail offline, and a field that starts as a row
   * and turns into chips once the answer lands has moved the form under the
   * person's thumb. So the server answers it here.
   *
   * Three answers, and the empty one is not the same as null:
   *
   *   RecordTargetRow[]  at most `INLINE_OPTION_LIMIT` rows, all nameable.
   *                      Draw them on the form.
   *   []                 the directory holds no rows at all. The sheet opens,
   *                      and it may offer to start the list.
   *   null               too many to draw, or the target binds no title, or one
   *                      row cannot be named. The sheet opens and searches.
   *
   * The empty case used to be folded into null, which left the picker unable to
   * tell "nothing to show you" from "too much to show you" — and so unable to
   * offer the one thing a person on their first day needs.
   *
   * This is also how a five-row Payment mode directory stops costing a
   * full-screen sheet, a keyboard and two taps.
   */
  targetRows: RecordTargetRow[] | null;
  /**
   * The name of `targetRecordId`, so the field can say which list it opens.
   *
   * The field is handed an id and has no way to look one up. `target_record` in
   * `props` is a record id and not a word, so it cannot be shown to anybody.
   * Null on every field that is not a reference, and on one with no target.
   */
  targetName: string | null;
  /**
   * What a new row in `targetRecordId` must be given, so the picker knows
   * whether adding one can be a single tap or has to be a form.
   *
   *   'title'     the title binding is the only live field. One tap is the
   *               whole row, and there is nothing else to offer.
   *   'optional'  there are more fields, none of them required. One tap, and a
   *               way through to the rest for whoever wants it.
   *   'required'  a live field other than the title is required, or the target
   *               binds no title at all. A one-tap write would be refused on
   *               the next push, silently and long after the sheet closed, so
   *               the form is the only honest answer.
   *
   * Null on every field that is not a reference, and on one with no target.
   */
  /**
   * The recipe of a computed field, from `app.field.spec`. Null on every other
   * type.
   *
   * The device needs it, not just the server: Section 5.7 rule 6 says the
   * device runs the same rule, so the phone must hold the tree to work the
   * value out offline and to draw a live preview while the person types.
   */
  spec: FieldSpec | null;
  /**
   * What this field STARTS with on a new entry, from `app.field.default_expr`.
   * Null on a field that starts with nothing, which is nearly all of them.
   *
   * The same tree a worked-out field keeps, and the difference is who owns the
   * answer: a formula owns its value forever, and a default hands one over. It
   * fills when the form opens and again when a field it reads changes, until
   * the person types into the field — from then on the value is theirs.
   *
   * The form owns the fill, so the device needs the tree: the server validates
   * and stores it and never applies it on write.
   */
  startsWith: FormulaSpec | null;
  /**
   * When this field shows on the form, from `app.field.default_expr.showWhen`
   * (Decision 11 of the lines plan): a yes-or-no over the typed fields of the
   * row and one hop. Null on a field that always shows. A hidden field saves
   * no value and is never required; `isShown` in formula.ts is the rule, and
   * the server runs it too.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  showWhen?: FormulaSpec | null;
  /**
   * When a person may change this field, from `app.field.default_expr.editableWhen`
   * (F10 of docs/plans/field-relations.md): a yes-or-no read like `showWhen`.
   * Null on a field that is always editable. A locked field still takes its
   * Starts with, and the server refuses a person's edit that changes it
   * (`FIELD_LOCKED`); `isEditable` in formula.ts is the rule.
   *
   * Optional on the wire, read it with `?? null`.
   */
  editableWhen?: FormulaSpec | null;
  /**
   * What this field must be, from `app.field.default_expr.mustBe` (F13 of
   * docs/plans/field-relations.md): each rule a yes-or-no, a message and a
   * level. `checkRules` runs them after the formulas, on the form and on the
   * server. Optional on the wire: read it with `?? []`.
   */
  mustBe?: readonly MustBeRule[] | null;
  /**
   * True when `startsWith` fills once, from `app.field.default_expr.once`
   * (F11): it fills when the entry or the line is added and never follows its
   * inputs after. Absent or false: the default follows its inputs until the
   * person types.
   */
  startsOnce?: boolean;
  /**
   * "The open row for this vehicle" (step 9.2 of docs/plans/ledger.md), from
   * `app.field.default_expr.openRow`: a reference field that starts with the
   * latest open row of its target whose `match` equals this entry's `by`. The
   * form asks `GET /records/:recordId/fields/:fieldId/open-row` when `by`
   * changes and fills the field until the person picks one. Absent or null
   * on nearly every field.
   */
  openRow?: OpenRowDefault | null;
  targetCreate: 'title' | 'optional' | 'required' | null;
  /**
   * `'record'` when this reference picks one entry of another record (a Guest
   * Service Order picks its Booking): the picker then asks the server's search
   * as well as the device, because a record is windowed, draws each entry's
   * date and amount under its title, and offers newest first. Absent or
   * `'directory'` on a pick from a list, and on a schema cached before it existed.
   */
  targetKind?: 'directory' | 'record';
  /**
   * The filter of a dependent choice, from `app.field.spec`: offer only the
   * rows of `targetRecordId` whose `field` equals this entry's `equals`. Null
   * on a field with no filter, and on every type but reference and
   * multi_select. `rowMatches` in formula.ts is the rule.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  filter?: ChoiceFilter | null;
  /**
   * The most rows a multi_select may hold, from `app.field.spec`. Null when
   * there is no limit, and on every other type. Optional on the wire for the
   * same reason as `filter`.
   */
  maxChosen?: number | null;
  /**
   * A `lines` field's lines record, the count field that starts its lines,
   * and the most it holds, from `app.field.spec` (Decision 4 of
   * docs/plans/entry-lines-and-layout.md). Null on every other type. The
   * lines record's own schema is in the response's `lineSchemas`, under this
   * field's id.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  lines?: LinesFieldSpec | null;
};

/** GET /v1/records/:recordId/schema?registry_version=NN */
export type RecordSchemaResponse = {
  recordId: string;
  name: string;
  fieldSetVer: number;
  nounKey: string;
  entryNounKey: string;
  currency: string | null;
  bindings: Partial<Record<PurposeId, RecordBindingAnswer>>;
  fields: RecordSchemaField[];
  /**
   * How this record's entries are written, or null for the ordinary form.
   *
   * Null is also the answer when the caller cannot see one of the fields the
   * setting names: a sheet that cannot draw its own list or its own mark is
   * not a sheet, and the honest fallback is the form, which this same payload
   * already describes in full. Section 11's masking rule, applied to a
   * setting rather than to a field.
   *
   * Optional, and absent means exactly what null means. `loadRecordSchema` is
   * the one assembly of this payload and it always answers the key, so nothing
   * on the wire is ever without it; the option is for the fixtures and stubs
   * that stand a schema up to test something else entirely, none of which has
   * an opinion about how entries are captured. Read `capture ?? null`.
   */
  capture?: RecordCapture | null;
  /**
   * Where each field of the form shows: sections, and the fields in each
   * (Decision 12 of docs/plans/entry-lines-and-layout.md). The server has
   * already taken out the node of every field this payload does not carry.
   *
   * Null when the record has no tree, when the stored tree does not read, or
   * when its version is newer than the server knows: the form is then drawn
   * in field order, as it always was. A device checks the version again
   * (`formPlan` does), because an old build can hold a newer schema in its
   * cache. Optional on the same terms as `capture`: read `layout ?? null`.
   */
  layout?: RecordLayout | null;
  /**
   * What this record holds, in one word, and the EFFECTIVE answer as the
   * catalog's own `icon` is: the stored name, or the default for a record that
   * was never given one. Decision 3 of docs/plans/done/record-icons-and-list-tiles.md.
   *
   * The schema carries it as well as the catalog because a record is opened
   * from more than the home list — a link, a search, the assistant — and a
   * screen that has the schema should never have to ask the catalog what glyph
   * to draw.
   *
   * Optional on the same terms `capture` is: `loadRecordSchema` always answers
   * the key, and the option is for the fixtures that stand a schema up to test
   * something else. Read `icon ?? null`.
   */
  icon?: RecordIconId | null;
  /** The person's own word for one entry, or null. Read `entryNoun ?? null`. */
  entryNoun?: RecordEntryNoun | null;
  /**
   * The Must be rules on the whole entry, from `app.record.prefs.rules` (F13
   * of docs/plans/field-relations.md). A rule that no longer reads is left
   * out. Optional on the wire: read it with `?? []`.
   */
  rules?: readonly MustBeRule[] | null;
  /**
   * The pattern of the record's live money rule (L4 of docs/plans/ledger.md),
   * such as `adjustment`, or null for a record with none or whose Counts in
   * money is off. The form reads it to say before the save what the server
   * will do (L18). Optional on the wire: read it with `?? null`.
   */
  moneyPattern?: string | null;
  /**
   * Whether the live money rule makes each entry its own party (L46,
   * `ruleSelfParty`): an entry its own sale, the entries that pick it as their
   * party, and its payments are all owed on. Its page then reads its
   * statement (GET …/entries/:entryId/statement) and says what is still owed.
   * Optional on the wire: read it with `?? false`.
   */
  moneySelfParty?: boolean;
  views: Array<{ id: string; nameKey: string; type: string; spec: object; ord: number }>;
  /**
   * The schema of each lines record this record's `lines` fields hold, keyed
   * by the `lines` field's id, so a form draws its lines offline from the one
   * schema it already holds. A depth-1 schema holds its own `lineSchemas`.
   * A line schema leaves out the parent and root fields (the server sets
   * them; their bindings stay, so a line formula's hop through them is
   * answered from the form above) and every field this caller's access
   * masks. A lines record has no field_set_ver a device tracks on its own:
   * a change to it moves the field_set_ver of the records above it, so this
   * map is refreshed with them.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? {}`.
   */
  lineSchemas?: Record<string, RecordSchemaResponse>;
};

/* -------------------------------------------------------- structure editing */

/**
 * The customize-entry-flow API: one read, one write.
 *
 * This is the write side of Section 3 of docs/records-platform-architecture.md,
 * "what fully customizable means". Every knob below is already a column on
 * app.field; none of them had a screen until now.
 *
 * Two rules shape the shapes.
 *
 * 1. The editor reads more than the form does. GET /schema answers the live
 *    field set, so a switched-off field is absent from it. The editor must see
 *    that field to switch it back on, so it gets its own read.
 * 2. One save, one transaction. The screen collects a draft and saves once, so
 *    the write takes the whole change set: adds, patches, archives, the order,
 *    and the focused field. A half-applied structure is not a state the app
 *    should ever be able to reach.
 */

/** One field as the editor reads it. Every knob, switched on or off. */
export type StructureField = {
  id: string;
  label: string;
  type: ValueTypeId;
  /** primary is the template's and cannot be switched off. custom is the user's. */
  tier: 'primary' | 'secondary' | 'custom';
  enabled: boolean;
  required: boolean;
  sticky: boolean;
  ord: number;
  helpText: string | null;
  /** As stored, never resolved: the editor must show what was asked for. */
  component: ComponentId;
  props: Record<string, unknown>;
  storage: string;
  targetRecordId: string | null;
  /** Null on every type but select. */
  options: RecordFieldOption[] | null;
  unitList: string[] | null;
  /**
   * The recipe of a computed field, from `app.field.spec`. Null on every other
   * type.
   *
   * The device needs it, not just the server: Section 5.7 rule 6 says the
   * device runs the same rule, so the phone must hold the tree to work the
   * value out offline and to draw a live preview while the person types.
   */
  spec: FieldSpec | null;
  /** What the field starts with on a new entry, from `default_expr.formula`. */
  startsWith: FormulaSpec | null;
  /** When the field shows on the form, from `default_expr.showWhen`. Null: always. */
  showWhen?: FormulaSpec | null;
  /** When a person may change the field, from `default_expr.editableWhen`. Null: always. */
  editableWhen?: FormulaSpec | null;
  /** What the field must be, from `default_expr.mustBe` (F13). Absent or empty: no rule. */
  mustBe?: readonly MustBeRule[] | null;
  /** True when the field's Starts with fills once, from `default_expr.once`. */
  startsOnce?: boolean;
  /**
   * The purposes that name this field. A field the engine reads cannot be
   * switched off or archived, and this is what lets the screen say so before
   * the server refuses.
   */
  boundPurposes: PurposeId[];
};

/**
 * How many typed slots this record has left, per family.
 *
 * A record holds ten numeric slots, six text, four time, six reference, and
 * two boolean. Past that a new field lands in the attrs document, which cannot
 * hold `not null`, so it cannot be required. The screen needs both facts to
 * say something true before a person types a name.
 */
export type StructureCapacity = {
  number: number;
  text: number;
  date: number;
  reference: number;
  boolean: number;
};

/** GET /v1/records/:recordId/structure */
export type RecordStructureResponse = {
  recordId: string;
  name: string;
  nounKey: string;
  entryNounKey: string;
  currency: string | null;
  /** Bumped by every structure write. A draft carries it back to prove it is fresh. */
  fieldSetVer: number;
  /** False for a reader: the screen draws itself, and every control is quiet. */
  mayEdit: boolean;
  fields: StructureField[];
  bindings: Partial<Record<PurposeId, RecordBindingAnswer>>;
  capacity: StructureCapacity;
  /**
   * How this record's entries are written, straight off `app.record.prefs`.
   *
   * Unmasked, unlike the schema's copy: the editor reads the record whole, the
   * way it reads a switched-off field, because it is the screen that has to be
   * able to change the setting.
   */
  capture: RecordCapture | null;
  /**
   * Where each field shows, straight off `app.record.prefs.layout`, read with
   * every live field and unmasked, as `capture` is. Null when there is no tree
   * or it does not read. Optional so a fixture need not stand one up.
   */
  layout?: RecordLayout | null;
  /** The Must be rules on the whole entry, from `app.record.prefs.rules` (F13). Absent or empty: none. */
  rules?: readonly MustBeRule[] | null;
  /**
   * How deep this record sits in a tree of lines: 0 on a record or a list, 1
   * on a lines record whose parent is a record, 2 below that. The editor
   * offers a new `lines` field only while one more level fits
   * (`LINES_MAX_DEPTH`). Optional so a fixture need not stand one up; absent
   * reads as 0.
   */
  linesDepth?: number;
  /**
   * The icon this record was GIVEN, straight off `app.record.prefs`, and null
   * when it was never given one.
   *
   * The stored name, unlike the schema's and the catalog's copies, which both
   * answer the effective one. The editor is the screen that CHANGES the
   * setting, so it has to be able to say "Default" — and a default it was
   * handed as a name would be saved back as a choice the person never made.
   */
  icon: RecordIconId | null;
  /** The directories a reference field may point at, with their own words. */
  directories: Array<{ id: string; name: string }>;
  /**
   * The records a reference may pick one entry of (a Guest Service Order picks
   * its Booking), each titled by words. Absent from an older server.
   */
  records?: Array<{ id: string; name: string }>;
  /**
   * The person's own word for one entry, or null while the record says
   * `entryNounKey`. The server always answers it; optional for the fixtures.
   */
  entryNoun?: RecordEntryNoun | null;
  /**
   * How the totals work today, read off the `measure` and `sign` bindings.
   * The server always answers it; optional for the fixtures. Absent on a list.
   */
  totals?: RecordTotals;
};

/**
 * How a record's totals work, as Customize asks it: "How totals work".
 *
 *   money_in    the measure (a money field) adds to money in, every entry
 *   money_out   the same, to money out
 *   money_both  the measure, and a choice field whose choices each say in or
 *               out, so every entry says which
 *   count       a number or quantity field, counted and not money
 *   none        no amount and no total
 *
 * The same five answers `RecordBlankCounts` gives at creation. `measure` and
 * `sign` are field ids, or add keys of the same structure save, as
 * `focusedInput` is.
 *
 * Changing it moves every past entry with it: the totals are worked out again
 * from the entries in the same save. No entry is rewritten.
 */
export type RecordTotals =
  | { counts: 'money_in' | 'money_out' | 'count'; measure: string }
  | { counts: 'money_both'; measure: string; sign: string }
  | { counts: 'none' };

/** One option as the editor sends it. No id means a new choice. */
export type StructureOptionInput = {
  id?: string;
  label: string;
  /**
   * A new choice's key, which rules read it by (a kit's `cycles`): kept when it
   * is a short lower-case word no other choice of the field holds, else the
   * key is made from the label. A choice held keeps its own.
   */
  key?: string;
  sign?: 1 | -1 | null;
  /**
   * The colour the choice wears once it is chosen. `null` takes a tone off and
   * an absent key leaves whatever the choice already wears, the same rule
   * `startsWith` follows a few types down.
   */
  tone?: RecordCaptureTone | null;
};

/** A change to a field that already exists. Absent means leave it alone. */
export type StructureFieldPatch = {
  id: string;
  label?: string;
  helpText?: string | null;
  required?: boolean;
  enabled?: boolean;
  sticky?: boolean;
  component?: ComponentId;
  props?: Record<string, unknown>;
  options?: StructureOptionInput[];
  unitList?: string[] | null;
  /**
   * A new recipe for a computed field. It applies to entries saved from now on
   * and never backfills, which is the standing rule for a field change.
   */
  spec?: FieldSpec | null;
  /**
   * What the field starts with from now on. `null` clears it, which is the
   * whole reason this one is nullable: a person taking a default off has to be
   * able to say so, and an absent key already means "leave it alone".
   *
   * Entries already saved are untouched, like every other field change.
   */
  startsWith?: FormulaSpec | null;
  /**
   * When the field shows from now on: a yes-or-no rule. `null` takes it off,
   * so the field always shows, for the same reason `startsWith` is nullable.
   */
  showWhen?: FormulaSpec | null;
  /**
   * When a person may change the field from now on: a yes-or-no rule. `null`
   * takes it off, so the field is always editable.
   */
  editableWhen?: FormulaSpec | null;
  /**
   * The field's Must be rules from now on, the whole list (F13). `null` or an
   * empty list takes them off. Entries already saved are untouched (F16).
   */
  mustBe?: readonly MustBeRule[] | null;
  /** Whether the Starts with fills once (true) or follows its inputs (false). */
  startsOnce?: boolean;
};

/** A field that does not exist yet. */
export type StructureFieldAdd = {
  /** The client's handle for it, so `order` and `focusedInput` can name it. */
  key: string;
  label: string;
  type: ValueTypeId;
  /** Absent takes the value type's primitive, which every device can draw. */
  component?: ComponentId;
  props?: Record<string, unknown>;
  required?: boolean;
  sticky?: boolean;
  helpText?: string | null;
  targetRecordId?: string | null;
  options?: StructureOptionInput[];
  unitList?: string[] | null;
  /** Required on a `formula`, refused on every other type. */
  spec?: FieldSpec | null;
  /**
   * What the new field starts with on a new entry. Absent and null both mean
   * nothing, and the two are the same thing on a field that does not exist yet.
   */
  startsWith?: FormulaSpec | null;
  /** When the new field shows on the form. Absent and null both mean always. */
  showWhen?: FormulaSpec | null;
  /** When a person may change the new field. Absent and null both mean always. */
  editableWhen?: FormulaSpec | null;
  /** The new field's Must be rules (F13). Absent, null and empty all mean none. */
  mustBe?: readonly MustBeRule[] | null;
  /** True when the Starts with fills once, when the entry or line is added. */
  startsOnce?: boolean;
  /**
   * Required on a `lines` field, refused on every other type: the fields each
   * line holds. The server makes the lines record in the same save, with its
   * parent and root fields, so a lines field never names a record of its own.
   */
  lines?: StructureLinesAdd;
};

/**
 * The lines record a new `lines` field makes.
 *
 *   fields     what each line holds, at most `LINES_MAX_FIELDS`. A field of
 *              type `lines` among them makes a second level, and the planner
 *              refuses a third (`LINES_MAX_DEPTH`).
 *   countFrom  a `number` field on the same row, by id or by add key, whose
 *              value starts that many lines. Absent or null: none does.
 *   max        the most lines one row holds, 1 to `ENTRY_BATCH_MAX`. Absent
 *              takes `ENTRY_BATCH_MAX`.
 *   start      the empty lines a new entry opens with, 0 to `LINES_START_MAX`.
 *              Absent takes 0.
 */
export type StructureLinesAdd = {
  fields: StructureFieldAdd[];
  countFrom?: string | null;
  max?: number;
  start?: number;
};

/**
 * What a structure save answers with: the editor's truth, and the form's.
 *
 * The schema is here so a device does not have to wait for the next sync pull
 * to draw the form it has just changed. It is exactly what `GET /schema` would
 * answer this client, built in the same transaction as the write, so the
 * mirror can take it as it stands.
 *
 * A separate type rather than an optional field on `RecordStructureResponse`:
 * that type is also the GET answer and the draft's own `base`, and a schema
 * riding along inside a draft would sit there going stale.
 */
export type RecordStructureSaveResponse = {
  structure: RecordStructureResponse;
  schema: RecordSchemaResponse;
};

/** PATCH /v1/records/:recordId/structure */
export type RecordStructureRequest = {
  /** The version the draft was built on. A mismatch is refused, never merged. */
  fieldSetVer: number;
  add?: StructureFieldAdd[];
  update?: StructureFieldPatch[];
  archive?: string[];
  /** Every field of the record after the change, in form order. */
  order?: string[];
  /** A field id, an add key, or null to clear. Absent leaves it alone. */
  focusedInput?: string | null;
  /**
   * How this record's entries are written from now on.
   *
   * Every field it names is a field id, or an add key of this same request —
   * a person turning a record into a sheet in one save names the fields they
   * are adding beside it, exactly as `focusedInput` and `order` do. `null`
   * takes the setting off and an absent key leaves it alone, so a save that
   * only renames a field can never drop a sheet by not mentioning it.
   */
  capture?: RecordCapture | null;
  /**
   * Where each field of the form shows from now on (Decision 12 of
   * docs/plans/entry-lines-and-layout.md). A field node names a field id or
   * an add key of this same request, as `order` does. `null` takes the tree
   * off and the form goes back to field order; an absent key leaves it alone.
   * A tree that does not read is refused with the `LAYOUT_*` code of
   * `judgeLayout`, never stored.
   */
  layout?: RecordLayout | null;
  /**
   * What this record holds from now on, one name of `RECORD_ICON_IDS`.
   *
   * `null` puts the record back to its default glyph and an absent key leaves
   * whatever it wears alone, the same rule `capture` and `startsWith` follow: a
   * save that only renames a field can never take an icon off by not
   * mentioning it. A name outside the list is refused (`ICON_UNKNOWN`), never
   * stored and never guessed at.
   */
  icon?: RecordIconId | null;
  /** app.record.name. */
  name?: string;
  /**
   * What one entry is called from now on. `null` puts the record back to its
   * `entryNounKey`; an absent key leaves it alone. Refused on nothing but its
   * length: 1 to 30 letters for each form.
   */
  entryNoun?: RecordEntryNoun | null;
  /** How the totals work from now on. Absent leaves them alone. Refused on a list. */
  totals?: RecordTotals;
  /**
   * The Must be rules on the whole entry from now on, the whole list (F13 of
   * docs/plans/field-relations.md), kept in `app.record.prefs.rules`. `null`
   * or an empty list takes them off; an absent key leaves them alone.
   */
  rules?: readonly MustBeRule[] | null;
};

/**
 * POST /v1/records/:recordId/rules/count: how many saved entries a Must be
 * rule being made would break (F16 of docs/plans/field-relations.md). A new
 * rule never touches them; Customize says how many there are, and an edit of
 * one must keep a block rule before it saves.
 *
 *   formula  the rule's yes-or-no tree
 *   fieldId  the field it sits on, so a field Show when hides skips it; absent
 *            for a rule on the whole entry
 */
export type RuleCountRequest = { formula: FormulaSpec; fieldId?: string };

/** `checked` entries were read, newest first, at most `RULE_COUNT_MAX`; `more` when the record holds more. */
export type RuleCountResponse = { checked: number; broken: number; more: boolean };

/** The most saved entries one rule count reads. */
export const RULE_COUNT_MAX = 2000;

/** One refusal. `fieldId` carries an add key when the problem is about a new field. */
export type StructureProblem = {
  code: string;
  fieldId: string | null;
  message: string;
};

/** The 400 body a refused structure write answers with. Every problem, not the first. */
export type RecordStructureProblemsResponse = {
  error: { code: 'STRUCTURE_INVALID'; message: string; problems: StructureProblem[] };
};

/* ------------------------------------------------------------------ entries */

/**
 * One value as the entry API carries it, keyed by app.field.id.
 *
 * Structurally identical to the server's internal EntryValue in
 * apps/server/src/entry-values.ts, and declared again here on purpose: that
 * module is the storage router and owns the slot vocabulary, this one is the
 * transport contract the device compiles against. Neither imports the other.
 */
export type EntryValue = string | number | boolean | readonly string[] | null;

export type EntryValues = Readonly<Record<string, EntryValue>>;

/**
 * POST /v1/records/:recordId/entries, and the body of PATCH .../:entryId.
 * entryId/clientOpId are present on offline-first creates: the device names
 * the entry while offline and pushes later, so a retried push is a replay,
 * not a second entry.
 */
export type EntryWriteRequest = {
  values: EntryValues;
  /** Device-generated logical entry id. When present, a unique conflict is a replay, not an error. */
  entryId?: string;
  /** Device-generated op id, for replay detection and support logs. */
  clientOpId?: string;
  /**
   * The lines of this entry, keyed by the id of the `lines` field that holds
   * them. Written in the same transaction as the entry. An edit sends the full
   * set for each field it names: a line left out is removed with its own
   * lines, and a field it does not name keeps its lines.
   */
  lines?: EntryLinesWrite;
  /**
   * An edit only: the version of the entry the edit was read at. When the
   * entry has moved since, the edit is refused (409 ENTRY_MOVED), so a set of
   * lines read earlier never removes a line someone added after it.
   */
  baseVersion?: number;
  /**
   * A create only (L64.5 of docs/plans/ledger.md): keep the entry as a draft
   * that waits for a row its writer may not add. `fieldId` is the pick left
   * empty, `name` the row as the person said it. The server decides: it is
   * refused when the writer may add the row themselves, or when a row of that
   * name is there; otherwise the entry saves as a draft that counts in
   * nothing, and the owner's Apply adds the row and saves it.
   */
  waitsForRow?: EntryRowWaitRequest;
};

/** The row a draft waits for, as a create asks for it. */
export type EntryRowWaitRequest = { readonly fieldId: string; readonly name: string };

/**
 * A draft that waits for a row (L64.5): the row as the person said it, the
 * list it goes on, and who adds it (the owner, by name). The words a screen
 * says: "Saved as a draft: waiting for Asha to add Generator diesel".
 */
export type EntryRowWait = {
  readonly fieldId: string;
  readonly listId: string;
  readonly listName: string;
  readonly name: string;
  /** The owner's name; null when the workspace has none to say. */
  readonly owner: string | null;
};

/**
 * How deep lines go. A depth-1 line belongs to an entry; a depth-2 line
 * belongs to a depth-1 line (a guest of a room of a stay). The planner refuses
 * a third level.
 */
export const LINES_MAX_DEPTH = 2;

/**
 * The most lines one save holds, across both levels. Without it, fifty rooms
 * of fifty guests would be 2,500 entries in one transaction. A batch holds at
 * most this many across all of its entries.
 */
export const LINES_MAX_PER_ENTRY = 200;

/**
 * The most fields one lines record holds, beside the parent and root fields
 * the server makes. A line is a short row, not a second form.
 */
export const LINES_MAX_FIELDS = 8;

/**
 * One line as a write carries it. The device names `entryId`, as it names an
 * entry it saves offline, so a retried save is a replay. `lines` is present
 * only on a depth-1 line whose record holds lines of its own.
 */
export type LineWrite = {
  readonly entryId: string;
  readonly values: EntryValues;
  readonly lines?: EntryLinesWrite;
};

/** The lines of one row, keyed by the id of the `lines` field that holds them. */
export type EntryLinesWrite = Readonly<Record<string, readonly LineWrite[]>>;

/** Nothing in a value a line sends: none, a blank string, an empty list. A zero and a false are values. */
const isBlankLineValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

/**
 * The lines of a write without the ones that hold nothing: every value the
 * line sends is blank, and no line under it is left once its own empty lines
 * are left out. Decided 5 Oct 2026: such a line is not saved, on the phone and
 * on the web alike, so both ask this one function.
 *
 * A write carries typed values only, so a line holding nothing but a
 * worked-out answer is empty here, and one holding a filled default is not.
 * On a create an empty line is never made. On an edit, a saved line the person
 * emptied is left out of its run, and a live line a named run leaves out is
 * one the server tombstones. So every run keeps its key, with whatever is left
 * in it, an empty run included.
 */
export const withoutEmptyLines = (lines: EntryLinesWrite): EntryLinesWrite => {
  const out: Record<string, readonly LineWrite[]> = {};
  for (const [linesFieldId, run] of Object.entries(lines)) {
    out[linesFieldId] = run.flatMap((line): LineWrite[] => {
      const under = line.lines === undefined ? undefined : withoutEmptyLines(line.lines);
      const holdsLines = under !== undefined && Object.values(under).some((one) => one.length > 0);
      if (!holdsLines && Object.values(line.values).every(isBlankLineValue)) return [];
      return [under === undefined ? line : { ...line, lines: under }];
    });
  }
  return out;
};

/**
 * The noun key a lines record's lines are called by, from its own
 * `entryNounKey`. A lines record is made with its field and carries the
 * default entry noun, and a line is not an entry (Decision 19 of
 * docs/plans/entry-lines-and-layout.md): with no key of its own a line is a
 * "line", on the phone and on the web alike. Words an owner set
 * (`entryNoun`) come before either.
 */
export const lineNounKeyOf = (entryNounKey: string | undefined): string =>
  entryNounKey === undefined || entryNounKey === 'noun.entry' ? 'noun.line' : entryNounKey;

/**
 * One line as a read carries it: the same tree a write sends, so a form
 * reads what it saved. A depth-2 line has no `lines`.
 */
export type LineRead = {
  readonly entryId: string;
  readonly values: EntryValues;
  readonly lines?: EntryLinesRead;
};

/** The lines of one row on a read, keyed by `lines` field id. */
export type EntryLinesRead = Readonly<Record<string, readonly LineRead[]>>;

export type EntryWriteResponse = {
  entryId: string;
  versionId: string;
  version: number;
  serverSeq: number;
  /**
   * What this save's links did, when a link's rule held: the rows they posted
   * and the ones they could not write. Absent when no rule held, and on a
   * replay (the first answer carried it). A rule that did not hold is said
   * nowhere (L3 of docs/plans/links-fixes.md). Not `links`: that word is the
   * reference rows an entry points at.
   */
  linkFirings?: EntryLinkOutcome;
  /**
   * What the save kept but a rule did not like (F14 of
   * docs/plans/field-relations.md): a `warn` rule that said no, a remote
   * `block` the server saved over (and raised a Needs you task for), and a
   * remote lock the save changed. Absent when there is none, and on a replay.
   */
  warnings?: EntryWarning[];
  /**
   * The status the save left the entry in (L55 of docs/plans/ledger.md):
   * `submitted` when the record's approval chain says it waits, so the form
   * says "Sent for approval" rather than "Saved". A replay says `submitted`
   * while the entry still waits. Absent when the save left it approved (it
   * counts at once), and from an older server.
   */
  status?: EntryStatusValue;
  /** A draft kept waiting for a row (L64.5): what it waits for. Absent on every other save. */
  waitsForRow?: EntryRowWait;
};

/**
 * One thing a save kept and says.
 *
 *   ENTRY_RULE_WARN    a Must be rule of level `warn` said no
 *   ENTRY_RULE_HELD    a remote Must be `block` said no on the server; the
 *                      entry is saved and a Needs you task asks someone to look
 *   FIELD_LOCKED_HELD  a remote Editable when rule locked a field the save
 *                      changed; saved, with a Needs you task (F10)
 */
/**
 * `MONEY_RULE_HELD`: the record's money rule cannot count this entry, so it
 * was saved as a draft and the owner asked to fix the rule (L58 of
 * docs/plans/ledger.md).
 */
export type EntryWarningCode = 'ENTRY_RULE_WARN' | 'ENTRY_RULE_HELD' | 'FIELD_LOCKED_HELD' | 'MONEY_RULE_HELD';
export type EntryWarning = {
  code: EntryWarningCode;
  /** The field the rule sits on, or null for a rule on the whole entry. */
  fieldId: string | null;
  /** The rule's own message with its values filled in, or the server's words for a lock. */
  message: string;
  /** The line it is on, when it is on a line and not the entry. */
  lineEntryId?: string;
};

/** One row a link of the saved entry wrote, changed or took out. */
export type EntryLinkPosted = {
  linkId: string;
  toRecordId: string;
  /** Null when the person saving may not open the record it went into. */
  toRecordName: string | null;
  did: 'added' | 'changed' | 'removed';
};

/**
 * One link whose rule held but which could not write its row. The save still
 * stands; this is said every time, with why and what to do.
 */
export type EntryLinkRefused = {
  linkId: string;
  /** The record the link belongs to, whose Links page changes it. */
  fromRecordId: string;
  /** Its name, so the words can say where the link is changed. */
  fromRecordName: string | null;
  toRecordId: string;
  /** Null when the person saving may not open the record it was to go into. */
  toRecordName: string | null;
  /** The firing's code: `TARGET_UNKNOWN`, `TARGET_MODE_UNSUPPORTED`, `MAP_INVALID:<problem>`. */
  code: string;
  /** Why, and what to change, in the server's words. */
  reason: string;
  /** Whether the person saving may change links, so the words offer the fix or say whom to ask. */
  mayFix: boolean;
};

export type EntryLinkOutcome = {
  posted: EntryLinkPosted[];
  refused: EntryLinkRefused[];
};

/**
 * GET /v1/records/:recordId/entries/:entryId/links — what the entry's links did
 * the last time it was saved: the rows they hold now, and the links that held
 * but could not write. The entry page says both.
 */
export type EntryLinksResponse = EntryLinkOutcome;

/**
 * GET and PATCH /v1/records/:recordId/money: "Counts in money" (L2 of
 * docs/plans/ledger.md). A record that is off counts in no money total: its
 * rule is not read, its earlier money is reversed, and its page says "Not in
 * money totals" under the headline. Turned on again, its entries count again.
 */
export type RecordMoneyResponse = {
  countsInMoney: boolean;
  /** Whether the record has a rule that counts its entries; one without has nothing to turn on. */
  hasRule: boolean;
  /** Whether this person may turn it on or off: whoever makes records. */
  mayChange: boolean;
};

export type RecordMoneyRequest = { countsInMoney: boolean };

/**
 * One account this entry moved, netted over its live postings (L26): "Cash
 * went up by ₹1,800", "Kavya Clinic owes ₹1,200 more". Words are the client's;
 * the server sends what moved and which way.
 */
export type MoneyEffectLine = {
  /** The ledger_account id. */
  accountId: string;
  /** Its row in the Accounts list, so the line can open it. */
  accountEntryId: string;
  /** The row's name; null when this reader may not read that row (a closed list). */
  accountName: string | null;
  type: LedgerAccountType;
  /** A contra account (Discounts given): its natural side is the other one. */
  contra: boolean;
  systemKey: string | null;
  /** The party the line is about, and its name when this reader may read it. */
  partyId: string | null;
  partyName: string | null;
  /** Up: the account grew on its natural side (cash in hand, an expense, what a customer owes). */
  direction: 'up' | 'down';
  /** How much, always positive, as an exact decimal. */
  amount: string;
  /** The currency code, or a quantity unit. */
  unit: string;
  /** The line a record's headline reads (L50). */
  main: boolean;
  /**
   * The balance now: of the party on this account when the line names one,
   * else of the account. Null unless the reader may see the money totals (`see_money`, L27).
   */
  balance: string | null;
};

/**
 * GET /v1/records/:recordId/entries/:entryId/postings: what this entry did to
 * the money, in business words (L26), to anyone who may see its amount (L27).
 *
 *   posted     the live effect of the version that stands
 *   waiting    a submitted entry posts nothing until it is approved (L10, L18);
 *              `lines` says what it would do
 *   none       nothing moves: no rule, a draft, an entry of nought, or a
 *              record whose Counts in money is off
 *   hidden     an amount the rule reads is hidden from this reader
 */
export type EntryPostingsResponse = {
  state: 'posted' | 'waiting' | 'none' | 'hidden';
  lines: MoneyEffectLine[];
  /** Whether the reader may see balances (`see_money`). */
  seesMoney: boolean;
  /**
   * The entry that corrects this one (L12, step 6.1): a closed entry is
   * corrected once, and its own effect then stands reversed. Null when none.
   * Optional for a server that predates it.
   */
  correctedBy?: string | null;
  /** The entry in a closed period this one corrects, when it is a correction. */
  corrects?: string | null;
  /**
   * The correction of this entry, said on the page (step 12.7): "Corrected by
   * Meena on 2 Nov", which opens it. `on` is the day its money moved. Null
   * when none. Optional for a server that predates it.
   */
  correction?: { readonly entryId: string; readonly by: string | null; readonly on: string | null } | null;
  /** The entry this one corrects, and the day it counted on, to open it: "Corrects the entry of 12 Feb". */
  corrected?: { readonly entryId: string; readonly on: string | null } | null;
};

/**
 * POST /v1/records/:recordId/entries/:entryId/correct (L12, step 6.1): an
 * entry dated inside a closed period is corrected, not edited or removed. The
 * body is a create's: the values the entry should have held, and its lines.
 * The server writes a new entry in the same record, dated today (the record's
 * date field is set to today whatever was sent), citing the old one
 * (`data.entry.reversal_of`). Its money reverses the old entry's effect,
 * dated today, and posts the right effect. Answers 201 with an
 * `EntryWriteResponse` for the new entry.
 *
 * Refused: ENTRY_NOT_CLOSED (409) when the entry's day is open, so it is
 * edited instead; ENTRY_ALREADY_CORRECTED (409) when a correction of it
 * stands; ENTRY_IS_DERIVED (409) for a row a link wrote; NOT_ALLOWED (403)
 * without add and change on the record; the create's own refusals otherwise.
 * A correction is never removed (ENTRY_IS_CORRECTION): it is changed, or
 * corrected in turn once its own day is closed.
 */
export type EntryCorrectRequest = {
  values: Record<string, unknown>;
  lines?: EntryLinesWrite;
  /** The new entry's id, so a retry is a replay. */
  entryId?: string;
};

/** What the ledger holds about one row of the Accounts list (L15). */
export type LedgerAccountRow = {
  accountId: string;
  /** Its row in the Accounts list. */
  entryId: string;
  type: LedgerAccountType;
  systemKey: string | null;
  openItem: boolean;
  contra: boolean;
  archived: boolean;
};

/**
 * GET /v1/ledger/accounts: the Accounts list and the kind of each row, so the
 * category picker can group by kind (L16). Answered to anyone who may open
 * the Accounts list; no balance rides on it.
 */
export type LedgerAccountsResponse = {
  /** The Accounts list's record id; null before the money kit made one. */
  recordId: string | null;
  accounts: LedgerAccountRow[];
};

/** One posting that moved an account, newest first (step 3.4). */
export type AccountMove = {
  postingId: string;
  entryId: string;
  recordId: string;
  /** Null when this person may not open that record: the row is drawn but not linked. */
  recordName: string | null;
  /** The entry's title in its own record, or null when it has none or is not open to this person. */
  entryTitle: string | null;
  /** The day it counts on, `YYYY-MM-DD`. */
  occurredOn: string;
  /** When it was written, an ISO instant. */
  at: string;
  /** What this posting did to the account, debit positive (L7). */
  amount: string;
  /** The account's balance after it, debit positive. */
  balanceAfter: string;
};

/**
 * GET /v1/records/:recordId/entries/:entryId/account: an Accounts row's
 * balance and the postings that moved it (step 3.4, L32). Needs `see_money`
 * (L27); a row of any other list answers 404.
 */
export type AccountRowResponse = {
  account: LedgerAccountRow;
  /** The workspace's currency. */
  unit: string;
  /** Debit positive. */
  balance: string;
  /** How many entries moved it, in all. */
  entries: number;
  moves: AccountMove[];
  /** True when older moves are held back. */
  more: boolean;
};

/** DELETE /v1/records/:recordId/entries/:entryId */
export type EntryDeleteResponse = { entryId: string; version: number };

/** The most entries one remove takes: the largest page the table shows. */
export const ENTRY_REMOVE_MAX = 500;

/**
 * POST /v1/records/:recordId/entries/delete — several entries at once, the
 * bulk bar's Remove.
 *
 * All or nothing, in one transaction. Every entry is checked first — it is
 * live, a link did not write it, and it is within the caller's reach of
 * Remove (their own entries only, for someone who sees only their own) — and
 * one refusal refuses the lot, with every problem named
 * (`EntryRemoveProblemsResponse`). A repeated id counts once.
 */
export type EntryRemoveRequest = { entryIds: string[] };

export type EntryRemoveResponse = { removed: EntryDeleteResponse[] };

export type EntryRemoveProblem = {
  entryId: string;
  code: 'ENTRY_UNKNOWN' | 'ENTRY_IS_DERIVED' | 'NOT_YOURS' | 'DIRECTION_NOT_ALLOWED' | 'ENTRY_UNREADABLE' | 'PERIOD_CLOSED' | 'ACCOUNT_IN_USE' | 'PARTY_IN_USE' | 'ENTRY_IS_CORRECTION';
  message: string;
};

/** The 409 body a refused remove answers with. */
export type EntryRemoveProblemsResponse = {
  error: { code: 'ENTRIES_REFUSED'; message: string; problems: EntryRemoveProblem[] };
};

/**
 * GET /v1/records/:recordId/export?format=csv&columns=a,b&filter=<json>, or
 * POST to the same path with an `EntryExportRequest` body when the filter is
 * too long for an address (a few hundred selected entries).
 *
 * The answer is the file itself, streamed: every entry this caller may read
 * that the filter keeps, newest first, read from the server and never from
 * what a device has loaded. A field this caller may not see is left out of
 * the file whether or not `columns` names it; `columns` absent means every
 * field they may see, in form order. A filter that names a field they may not
 * see is refused, never quietly dropped.
 */
export const ENTRY_EXPORT_FORMATS = ['csv'] as const;
export type EntryExportFormat = (typeof ENTRY_EXPORT_FORMATS)[number];

/**
 * One condition of an export filter. The same three shapes as the device's
 * list filter (`EntryFieldFilter` in apps/mobile/src/lib/entry-filter.ts), so
 * the table's filter travels as it stands: OR within one field, AND across
 * fields, and `none_of` keeps an entry that left the field empty.
 */
export type EntryExportCondition =
  | { fieldId: string; op: 'any_of' | 'none_of' | 'all_of'; values: (string | number)[] }
  | { fieldId: string; op: 'gte' | 'lte'; value: number }
  | { fieldId: string; op: 'set' | 'unset' }
  /** A date condition (F21 of docs/plans/field-relations.md), judged on the filter's `today`. */
  | { fieldId: string; op: DateConditionOp; value?: number | string };

export type EntryExportFilter = {
  /**
   * Calendar days on the record's time axis, `YYYY-MM-DD`, both inclusive.
   * The device resolves a preset ("this month") against its own today.
   */
  from?: string;
  to?: string;
  fields?: EntryExportCondition[];
  /**
   * The device's day, `YYYY-MM-DD`, that a date condition is judged on, so
   * the file holds the rows the table shows. Needed with a date condition.
   */
  today?: string;
  /** Words, as the table's search reads them. Matched against what the caller may see. */
  q?: string;
  /** Exactly these entries: "the selected ones". At most `ENTRY_REMOVE_MAX`. */
  entryIds?: string[];
};

export type EntryExportRequest = {
  format: EntryExportFormat;
  columns?: string[];
  filter?: EntryExportFilter;
};

export type EntryListItem = {
  entryId: string;
  version: number;
  values: EntryValues;
  /** data.entry_head.created_at. When the logical entry was born. */
  createdAt: string;
  /** The live version's created_at. Moves on every edit. */
  updatedAt: string;
  createdBy: string;
  sourceChannel: string;
  /**
   * The Link that wrote this row, and the entry it was written from. Null on a
   * row a person typed. Section 5.7 rule 2: the pair draws the Auto badge and
   * blocks a direct edit, so both travel with every row.
   */
  linkId: string | null;
  sourceEntryId: string | null;
  /**
   * The lines of this entry, keyed by `lines` field id, with depth-2 lines
   * under their depth-1 line. Absent on a record that holds no `lines` field.
   */
  lines?: EntryLinesRead;
  /**
   * The live version's status (L55 of docs/plans/ledger.md): `submitted` waits
   * for approval and counts in nothing yet, `draft` was sent back (or holds a
   * money rule that could not be kept). Absent when it is approved, and from
   * an older server.
   */
  status?: EntryStatusValue;
};

/**
 * GET /v1/records/:recordId/entries?limit=&cursor=&q=
 *
 * `references` is why the device can draw a row at all: the title binding of a
 * sales record is a reference field, so `values` carries a uuid and nothing
 * else. The server resolves each target's own title binding, because D6 says
 * the server sends meaning.
 */
/**
 * POST /records/:recordId/fields/:fieldId/to-list. The new list, and how many
 * entries moved their choice to its rows. A refusal comes back as ApiError.
 */
export type FieldToListResponse = {
  readonly list: { readonly id: string; readonly name: string };
  readonly fieldId: string;
  readonly converted: number;
};

export type EntryListResponse = {
  entries: EntryListItem[];
  /** Title of every row this page's reference slots point at, keyed by entry id. */
  references: Record<string, string>;
  /** base64url of { d, s } over (the time axis desc nulls last, server_seq desc). */
  cursor: string | null;
};

/** One field-level refusal, mirrored from the server's EntryValueProblem. */
export type EntryProblem = {
  code: string;
  /** Null when the problem is not about one field. */
  fieldId: string | null;
  message: string;
  /**
   * The line the problem is on, when it is on one of the entry's lines at
   * either level, never the entry itself. The form opens that line and marks
   * `fieldId` in it.
   */
  lineEntryId?: string;
};

/** The 400 body a refused write answers with. Every problem, not the first. */
export type EntryProblemsResponse = {
  error: { code: 'ENTRY_INVALID'; message: string; problems: EntryProblem[] };
};

/**
 * The most entries one batch may carry. Fifty is a sheet of a real crew, not a
 * round number: an attendance sheet writes one mark per person per day, and a
 * shop with more than fifty on it pushes two batches rather than one long
 * transaction holding a record's aggregate row.
 */
export const ENTRY_BATCH_MAX = 50;

/**
 * POST /v1/records/:recordId/entries/batch — 1 to ENTRY_BATCH_MAX creates in
 * one transaction, each checked exactly as a single POST is.
 *
 * `entryId` is REQUIRED here, unlike the single write: a batch is only ever
 * pushed by a device draining its own outbox, every op of which already named
 * its entry, and that name is what makes a retried batch a replay rather than
 * a second sheet of marks.
 */
export type EntryBatchItem = {
  entryId: string;
  /** Device-generated op id, for support logs. Validated and carried no further. */
  clientOpId?: string;
  values: EntryValues;
  /** The entry's lines, as on a single write. LINES_MAX_PER_ENTRY caps the batch as a whole. */
  lines?: EntryLinesWrite;
};

export type EntryBatchRequest = { entries: EntryBatchItem[] };

/** 201: one receipt per request entry, in request order. */
export type EntryBatchResponse = { entries: EntryWriteResponse[] };

/**
 * One refusal of a batch, and which entry of the REQUEST it is about.
 *
 * The index rides beside `fieldId` rather than inside it (`0:f_amount`) so the
 * phone reads a number where it expects a number and a field id where it
 * expects a field id: every existing reader of `EntryProblem` keeps working on
 * one of these, and the one new reader — the push, deciding which op to drop —
 * reads `entryIndex` without parsing anything.
 */
export type EntryBatchProblem = EntryProblem & { entryIndex: number };

/**
 * The 400 body a refused batch answers with. A batch is all or nothing, so one
 * bad entry rolls back every entry beside it.
 *
 * `code` is the same vocabulary a single write answers with — `ENTRY_INVALID`,
 * `DIRECTION_NOT_ALLOWED`, `ENTRY_ID_CONFLICT` — but the status is always 400,
 * where the single route would answer 403 or 409. The batch is an
 * optimisation and its only job on a refusal is to tell the phone to stop
 * batching: the push then replays those ops one at a time, and the single
 * route answers each one its true status.
 */
export type EntryBatchProblemsResponse = {
  error: { code: string; message: string; problems: EntryBatchProblem[] };
};

/* -------------------------------------------------------------------- sync */

/**
 * The offline-first sync contract. The server is the authority; the device
 * mirrors data and reconciles by cursor. Three pieces: a pull that carries
 * every entry version past the cursor, a websocket that only ever announces
 * that the cursor moved, and the idempotent create above that makes a
 * retried push safe. server_seq is dense per tenant, assigned by trigger, so
 * it is the one ordering both sides trust.
 */

/** POST /v1/sync/pull body. */
export type SyncPullRequest = {
  /** Device cursor: server_seq it has applied up to. 0 means full snapshot. */
  since: number;
  /** recordId -> field_set_ver the device holds; schemas that differ are resent in full. */
  schemaVersions: Record<string, number>;
  /**
   * The registry the device can draw, for the same component negotiation the
   * schema route performs. Absent means the floor, as there.
   */
  registryVersion?: number;
  /**
   * Ask for `remaining` in the answer. The device sends it on the first page
   * of a run only, because the count is a second read over the whole stream.
   */
  withRemaining?: boolean;
  /**
   * The oldest day the device wants rows for, `yyyy-mm-dd`. Honoured ONLY on a
   * full fill (`since` 0), and ignored on every later page.
   *
   * That restriction is the design, not a shortcut. The snapshot is the large
   * answer and the window pays there. The incremental stream is small, so every
   * version past the cursor travels whatever its day, and the device's own
   * prune decides what to keep. A day filter on the incremental stream would
   * silently strand a row the device already holds: an entry inside the window,
   * edited to a date outside it, whose new version would never arrive.
   *
   * A row with no day — a record with no time axis, so a directory — is never
   * filtered by this. The picker reads those lists, and they must arrive whole.
   */
  fromDay?: string;
  /**
   * True on every page of a run that began at cursor 0.
   *
   * `fromDay` is honoured only under this flag. `since === 0` cannot stand in
   * for it: a full fill is a hundred pages and only the first carries a zero
   * cursor, so a window keyed on the cursor would filter one page and let the
   * other ninety-nine through — on exactly the download it exists to shrink.
   */
  fullFill?: boolean;
};

/**
 * The response header that carries the size of the pull body as it was sent.
 *
 * A header and not `content-length`, because Android's HTTP client removes
 * `content-length` when it unzips a gzip body. The device adds this number up
 * to show how much of a first download has arrived.
 */
export const SYNC_BYTES_HEADER = 'x-alluzo-bytes' as const;

/** One entry version as the pull carries it: field-id keyed values, never slot columns. */
export type SyncEntryRow = {
  entryId: string;
  recordId: string;
  versionId: string;
  version: number;
  serverSeq: number;
  isCurrent: boolean;
  /** True on a tombstone version. values is empty on a tombstone. */
  deleted: boolean;
  values: EntryValues;
  createdBy: string;
  /** When this version was written. */
  createdAt: string;
  /**
   * When the entry was first added: its first version's time, the same on
   * every version. A version an order's write made (source channel `rollup`)
   * moves `createdAt`, never this. Absent from a server older than it.
   */
  addedAt?: string;
  sourceChannel: string;
  /** Set together on a derived row. Null on one a person typed. */
  linkId: string | null;
  sourceEntryId: string | null;
};

/**
 * GET /v1/search?q=&limit=&cursor=
 *
 * Every record of one business, searched on the server, so a person finds an
 * entry from three years ago that this phone stopped holding months back.
 *
 * It answers the same three questions the phone answers, in the same order:
 * the records whose own NAME matched, the entries whose own WORDS matched, and
 * the entries POINTING AT a row whose name matched. The third is why a wage row
 * is found by typing a worker's name — the row holds his id, and his own row
 * holds the name.
 *
 * NO WINDOW. It reads all of time. That was measured before it was decided: a
 * trigram index finds rows by their words rather than walking back through
 * days, so three years costs the same as one.
 */
export type SearchResponse = {
  /** Records whose own name matched, in catalog order. */
  records: { recordId: string; name: string }[];
  /** Entries that matched, newest first. `recordId` says which record to draw them as. */
  hits: SearchHitItem[];
  /** Titles for every reference slot the hits point at, keyed by entry id. */
  references: Record<string, string>;
  /** Where the next page starts, or null when this page was the last. */
  cursor: string | null;
};

/** One entry that matched, with the record it belongs to. */
export type SearchHitItem = EntryListItem & { recordId: string };

/** The record_headline aggregate for one record, for client reconciliation. */
export type SyncAggregateRow = {
  recordId: string;
  measureFieldId: string;
  value: number;
  asOfSeq: number;
};

/**
 * One DAY of one record, pre-summed: four numbers and nothing else.
 *
 * The `record_day` scope of Section 5.10, as one row on the wire. It exists so
 * a card whose window reaches past what the device still holds is answered from
 * a sum rather than from rows that are no longer there. A device that prunes
 * old entries keeps every bucket, because three years is about eleven hundred
 * days and three years of entries is half a million rows.
 *
 * **A day and not a month, and that is load bearing.** A card's window is built
 * out of a scope, and only `month` and `year` land on month boundaries; `day`,
 * `week` and every range a person drags out of the date picker do not. A month
 * bucket asked for 27 June to 3 July could only answer by counting days nobody
 * asked about. A day bucket answers every window exactly, and a series keyed by
 * day is the shape the app already draws.
 *
 * **Four numbers, because a net cannot be taken apart again.** A day with a 500
 * in and a 500 out nets to nought, and "money in today" is not nought. So the
 * two sides are counted as they are written, beside the net and the row count.
 *
 * `day` is `yyyy-mm-dd`, cut from the entry's OWN time axis and never from the
 * day it was written. A backdated entry belongs to the day it names.
 */
export type SyncBucketRow = {
  recordId: string;
  /** The record's measure field id, or null when it binds none and only the count travels. */
  measureFieldId: string | null;
  /** `yyyy-mm-dd`. */
  day: string;
  /** The measure, summed and signed. Nought when the measure is masked or absent. */
  value: number;
  /** How many entries that day. Never masked: the catalog already carries a record's count. */
  rows: number;
  /** The positive side of the measure, summed. */
  moneyIn: number;
  /** The negative side, summed as a positive number. */
  moneyOut: number;
  asOfSeq: number;
};

/** One picked entry's title, for a pick whose entry the device's window leaves out. */
export type SyncPickTitle = { entryId: string; recordId: string; title: string };

/**
 * How many entries a list's totals leave out because they pick no row of the
 * list (W11 of docs/plans/workbook-map.md, owner 29 Oct 2026): a job with no
 * customer adds to no customer's Billed, so a card of the customers' stored
 * Pending says so. Counted on the server because the device holds only a
 * window of the rows.
 *
 * One row per list, record added up (`overRecordId`) and SET of the list's
 * totals (rollup fields, `fieldIds`, sorted) that leave the same entries out:
 * the current, undeleted entries the caller may read whose pick is empty and
 * which meet each of those totals' own conditions. Each entry is in one row
 * of its list, so a card that stands on several totals adds the rows that
 * name any of them and counts every entry once. `viaFieldId` is the pick of
 * the first of them, for its label.
 *
 * `unjudged` marks a total whose conditions the server cannot judge (a choice
 * that is no longer one, a field taken off): its own row, `count` 0, and a card
 * that stands on it may leave out more than it says.
 */
export type SyncUnpickedRow = {
  recordId: string;
  fieldIds: string[];
  overRecordId: string;
  viaFieldId: string;
  count: number;
  unjudged?: true;
};

export type SyncPullResponse = {
  /** Every version with server_seq > since, ascending. Includes non-current versions and tombstones. */
  entries: SyncEntryRow[];
  /** The full readable catalog, every pull — it is small and it is how a record another member added arrives. */
  records: RecordListItem[];
  /** Full schemas for records whose field_set_ver differs from what the device sent (or that it does not know). */
  schemas: RecordSchemaResponse[];
  aggregates: SyncAggregateRow[];
  /**
   * The day buckets, sent ONLY on the page that catches the device up — the one
   * whose `hasMore` is false.
   *
   * Not cursor bounded, and deliberately not sent per page. `as_of_seq` on an
   * aggregate is the record's LATEST write, so on a half-million-row fill every
   * bucket passes `as_of_seq > since` on every page and the cursor bound saves
   * nothing. Worse, a bucket is a total as of the horizon while a mid-fill
   * device holds only part of the rows, so buckets that arrive early are ahead
   * of the mirror they will be read beside. Sending them when the run is caught
   * up makes the two agree by construction.
   */
  buckets: SyncBucketRow[];
  /** The complete set of record ids the caller may open. The device drops local data for anything absent. */
  readableRecordIds: string[];
  /** The server_seq the server evaluated up to — NOT the max row sent. Rows in ungranted records are filtered out; this keeps the cursor moving past them. */
  nextSince: number;
  /** True when the entries page was truncated; call again with since = nextSince. */
  hasMore: boolean;
  /**
   * How many entry versions the caller may read after this page, up to the
   * same horizon. Present only when the request set `withRemaining`.
   */
  remaining?: number;
  /**
   * The title of each entry this page's entries pick in a windowed record
   * (step 9.8 of docs/plans/field-relations.md): a picked booking the device
   * does not mirror is still named. Only what the caller may read of it;
   * absent when there is none.
   */
  pickTitles?: SyncPickTitle[];
  /**
   * The Dashboard cards the workspace's setup drafted across records, kept at
   * Build (`workspaceBoardCards`): only those whose every record the caller
   * may open. A device with no Dashboard of its own starts from them, before
   * the usual cards. Absent when there are none.
   */
  boardCards?: WorkspaceBoardCard[];
  /**
   * The entries each total of a list leaves out for picking no row of it
   * (`SyncUnpickedRow`), only those above none or not judged. Counted when
   * they may have moved: on the page that catches a full fill up, and on a
   * page of any other run whose entries are of a record a total adds up, or
   * whose schemas are of a list or such a record. The device replaces what it
   * kept with it; absent, it keeps what it holds.
   */
  unpicked?: SyncUnpickedRow[];
};

/** The one websocket message: a seq advanced. Never data. */
export type SyncEventMessage = { type: 'seq'; tenantId: string; seq: number };

/* -------------------------------------------------------------------------- */
/* Attachments                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The attachment contract. Three calls, in this order, and none of them carries
 * a byte of the file through this API.
 *
 *   POST /v1/attachments          ask for somewhere to put one file
 *   PUT  <the url that answered>  the phone sends the bytes to the store
 *   POST /v1/attachments/:id/commit   prove it landed, and claim it
 *
 * Only a committed attachment id may be written into an entry. An id that was
 * asked for and never committed is deleted by the store's own lifecycle rule,
 * so an abandoned upload costs nothing and leaves nothing behind.
 */

/** What the phone knows before it uploads. */
export type AttachmentCreateRequest = {
  /** Which record the file belongs to. Decides the caller's permission. */
  recordId: string;
  /** Which field will name it. Decides the type the template allows. */
  fieldId: string;
  /** What the person's device calls it. Shown in a row, never used as a key. */
  fileName: string;
  mimeType: string;
  /** What the phone measured. Checked again at commit against what landed. */
  sizeBytes: number;
};

export type AttachmentCreateResponse = {
  /** The id to commit, and later the id the entry stores. */
  id: string;
  /** PUT the bytes here. */
  uploadUrl: string;
  /** Send exactly these headers with the PUT. They are part of the signature. */
  uploadHeaders: Record<string, string>;
  /** ISO 8601. After this the link is refused and a new one must be asked for. */
  expiresAt: string;
};

/** One attachment, as every read of one answers. */
export type AttachmentSummary = {
  id: string;
  recordId: string;
  fieldId: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  /** The stored image's size in pixels. Null for anything that is not an image. */
  width: number | null;
  height: number | null;
  /** A short-lived link to the bytes. Null until the upload is committed. */
  readUrl: string | null;
  /**
   * A short-lived link to the small copy, for a list row. Null for a file with
   * no thumbnail, which today means a PDF.
   *
   * A row draws THIS and never `readUrl`: a list of thirty receipts must not
   * download thirty photographs.
   */
  thumbnailUrl: string | null;
  /** ISO 8601, when both links stop working. Null before the upload is proven. */
  readUrlExpiresAt: string | null;
};

export type AttachmentCommitResponse = { attachment: AttachmentSummary };

/** GET /v1/attachments?ids=a,b,c — what the entry form needs to draw its rows. */
export type AttachmentListResponse = { attachments: AttachmentSummary[] };

export type AttachmentDeleteResponse = { id: string; deleted: true };

/* ------------------------------------------------------------ approvals */

/*
 * Approvals (Phase 11 of docs/plans/ledger.md, L54 to L57). A record that needs
 * approval has one live chain: which entries need approval, then the steps in
 * order. Amounts are in the record's currency, in whole units as typed (50000
 * is fifty thousand), compared with the entry's amount, its sign set aside.
 *
 *   GET    /v1/records/:recordId/approval-chain       ApprovalChainResponse
 *   PUT    /v1/records/:recordId/approval-chain       ApprovalChainWrite -> ApprovalChainResponse
 *   DELETE /v1/records/:recordId/approval-chain       ApprovalChainResponse (chain null)
 *   GET    /v1/entries/:entryId/approval              EntryApprovalResponse
 *   POST   /v1/entries/:entryId/decisions             EntryDecisionWrite -> EntryDecisionResponse
 *   POST   /v1/entries/decisions                      EntryDecisionsWrite -> EntryDecisionsResponse
 *   GET    /v1/approvals/waiting                      ApprovalsWaitingResponse
 *   GET    /v1/records/:recordId/entries?waiting=me   the record's entries that wait for the caller
 *   GET    /v1/records/:recordId/approvals            RecordApprovalsResponse (the list's marks)
 *
 * Send again: the writer PATCHes the sent-back entry (an empty `values` sends
 * it as it is), and the chain starts again from step 1.
 */

/** Who decides one step. */
export type ApprovalWho =
  /** The writer's manager on Team (Reports to), climbing past the writer and anyone who already approved, then the owner. */
  | { readonly kind: 'manager' }
  /** Anyone who holds this workspace role (app.workspace_role.id). */
  | { readonly kind: 'role'; readonly roleId: string }
  /** One named member. */
  | { readonly kind: 'member'; readonly userId: string }
  /** The owner or any admin. Met at once when the writer is one (L18). */
  | { readonly kind: 'owner_or_admin' };

export type ApprovalStepRule = {
  readonly who: ApprovalWho;
  /** The step applies only to an entry above this amount. Null: every entry the chain catches. */
  readonly above: number | null;
};

/** Which entries need approval. Any condition that is on catches an entry; all off catches none. */
export type ApprovalConditions = {
  /** Every entry. */
  readonly every: boolean;
  /** An entry above this amount. Null: off. */
  readonly above: number | null;
  /** An entry by a person whose access to this record says "Needs approval" (write_mode submit_draft). */
  readonly byRole: boolean;
};

/** PUT /v1/records/:recordId/approval-chain. At least one step, at most APPROVAL_STEPS_MAX. */
export type ApprovalChainWrite = {
  readonly conditions: ApprovalConditions;
  readonly steps: readonly ApprovalStepRule[];
};

export type ApprovalChainRead = ApprovalChainWrite & {
  /** Moves on every save; an entry keeps the version it started under. */
  readonly version: number;
  readonly updatedAt: string;
};

export type ApprovalChainResponse = {
  readonly recordId: string;
  readonly chain: ApprovalChainRead | null;
  /** The roles whose access to this record says "Needs approval" (write_mode submit_draft), for the condition's words. */
  readonly needsApprovalRoles: readonly { readonly roleId: string; readonly name: string }[];
};

/** The most steps one chain holds. */
export const APPROVAL_STEPS_MAX = 5;
/** The most entries one approve-several call decides. */
export const APPROVAL_BATCH_MAX = 50;
/** The longest reason a send back keeps. */
export const APPROVAL_REASON_MAX = 500;

export type ApprovalPerson = { readonly userId: string; readonly name: string | null };

/** One step of the chain an entry runs, as it stands for this entry. */
export type ApprovalStepState = {
  /** Zero-based, in chain order. */
  readonly index: number;
  readonly who: ApprovalWho;
  readonly above: number | null;
  /**
   * approved   decided
   * waiting    the step the entry waits at
   * later      a step still to come
   * skipped    does not apply to this entry (its amount), or was met at once (an admin's own entry)
   */
  readonly state: 'approved' | 'waiting' | 'later' | 'skipped';
  /** Who may decide it: for the waiting step, the people its tasks went to. Empty for a decided or skipped step. */
  readonly approvers: readonly ApprovalPerson[];
  readonly decidedBy: ApprovalPerson | null;
  readonly decidedAt: string | null;
};

/** One decision in the entry's history, oldest first. */
export type ApprovalDecisionRead = {
  readonly step: number;
  readonly decision: 'approve' | 'send_back';
  readonly reason: string | null;
  readonly by: ApprovalPerson;
  readonly at: string;
  /** The entry version decided. */
  readonly version: number;
  /** An approve the save wrote: an owner's or an admin's own entry met this step when it was saved (L63). */
  readonly atSave: boolean;
};

/** GET /v1/entries/:entryId/approval: where the entry stands. */
export type EntryApprovalResponse = {
  readonly entryId: string;
  readonly recordId: string;
  readonly status: EntryStatusValue;
  readonly version: number;
  /** The chain version the entry runs now. Null when it waits for nobody. */
  readonly chainVersion: number | null;
  /** The steps of that chain for this entry. Empty when it waits for nobody. */
  readonly steps: readonly ApprovalStepState[];
  /** Who it waits for now. Empty unless `status` is submitted. */
  readonly waitingFor: readonly ApprovalPerson[];
  /** The last send back, while the entry is a draft because of it. */
  readonly sentBack: { readonly reason: string; readonly by: ApprovalPerson; readonly at: string } | null;
  /** Every decision on the entry, every version. */
  readonly decisions: readonly ApprovalDecisionRead[];
  /** Whether the caller may approve or send it back now. */
  readonly mayDecide: boolean;
  /** Who sent it for approval, and when; null when it never waited. */
  readonly sentBy: ApprovalPerson | null;
  readonly sentAt: string | null;
  /** Which of the chain's conditions caught it, while it waits. Null otherwise. */
  readonly why: ApprovalWhy | null;
};

/** Which condition caught an entry: every entry, its amount, or the writer's "Needs approval". */
export type ApprovalWhy = { readonly kind: 'every' } | { readonly kind: 'above'; readonly amount: number } | { readonly kind: 'role' };

/** One entry of a record that waits, or was sent back, as the record's list marks it. */
export type RecordApprovalMark = {
  readonly entryId: string;
  /** `waits_row`: a draft kept waiting for a row its writer may not add (L64.5); `waitsForRow` says which. */
  readonly state: 'waiting' | 'sent_back' | 'waits_row';
  /** 1-based, among the steps that apply to this entry. Null when sent back. */
  readonly stepAt: number | null;
  readonly stepCount: number;
  readonly waitingFor: readonly ApprovalPerson[];
  readonly mayDecide: boolean;
  readonly sentBackBy: ApprovalPerson | null;
  /** A `waits_row` draft's row, by name, and the list it goes on. Absent on every other mark. */
  readonly waitsForRow?: { readonly name: string; readonly listName: string };
};

/** GET /v1/records/:recordId/approvals. */
export type RecordApprovalsResponse = { readonly recordId: string; readonly entries: readonly RecordApprovalMark[] };

/** POST /v1/entries/:entryId/decisions. A send back needs a reason. */
export type EntryDecisionWrite = {
  readonly decision: 'approve' | 'send_back';
  readonly reason?: string;
  /** The entry version the person read. A decision on an entry that moved since is refused (ENTRY_MOVED). */
  readonly version?: number;
};

export type EntryDecisionResponse = {
  readonly entryId: string;
  /** submitted while a later step waits; approved after the last; draft after a send back. */
  readonly status: EntryStatusValue;
  readonly versionId: string;
  readonly version: number;
  readonly serverSeq: number;
  /** Who it waits for next. Empty unless still submitted. */
  readonly waitingFor: readonly ApprovalPerson[];
};

/** POST /v1/entries/decisions: approve several at once (a record's "Waiting for me"). Each entry is decided on its own. */
export type EntryDecisionsWrite = { readonly entryIds: readonly string[]; readonly decision: 'approve' };

export type EntryDecisionsResponse = {
  readonly results: readonly (
    | { readonly entryId: string; readonly ok: true; readonly status: EntryStatusValue }
    | { readonly entryId: string; readonly ok: false; readonly code: string; readonly message: string }
  )[];
};

/** GET /v1/approvals/waiting: what waits for the caller, for the Dashboard card and the sidebar counts. */
export type ApprovalsWaitingResponse = {
  readonly total: number;
  readonly records: readonly { readonly recordId: string; readonly count: number }[];
  /** Oldest first. */
  readonly entries: readonly ApprovalWaitingEntry[];
};

/** One entry waiting for the caller. */
export type ApprovalWaitingEntry = {
  readonly entryId: string;
  readonly recordId: string;
  readonly recordName: string;
  /** Zero-based chain index of the step it waits at. */
  readonly step: number;
  /** 1-based, among the steps that apply to this entry, and how many apply. */
  readonly stepAt: number;
  readonly stepCount: number;
  readonly since: string;
  readonly writer: ApprovalPerson;
  /** The entry's amount as the chain compares it; null when the record binds none. */
  readonly amount: number | null;
  /** When the entry was first made. */
  readonly createdAt: string;
  /**
   * The live version that waits, sent back with a decision so one made on an
   * entry edited since the list was read is refused as moved.
   */
  readonly version: number;
};

/**
 * The refusals of the approval routes, beside ENTRY_UNKNOWN, ENTRY_MOVED and
 * NOT_ALLOWED:
 *
 *   APPROVAL_CHAIN_INVALID   the chain's shape (a step with nobody, too many steps)
 *   APPROVAL_NOT_WAITING     the entry waits for nobody
 *   APPROVAL_OWN_ENTRY       nobody approves their own entry
 *   APPROVAL_ONE_STEP        one person approves at most one step of an entry
 *   APPROVAL_NOT_YOURS       the step waits for somebody else
 *   APPROVAL_NOT_WRITER      only its writer takes a waiting entry back (step 12.7)
 *   APPROVAL_REASON_NEEDED   a send back says why
 */
export const APPROVAL_REFUSALS = [
  'APPROVAL_CHAIN_INVALID',
  'APPROVAL_NOT_WAITING',
  'APPROVAL_OWN_ENTRY',
  'APPROVAL_ONE_STEP',
  'APPROVAL_NOT_YOURS',
  'APPROVAL_REASON_NEEDED',
  'APPROVAL_NOT_WRITER',
  'APPROVAL_CHAIN_MOVED',
] as const;
export type ApprovalRefusal = (typeof APPROVAL_REFUSALS)[number];

/* ---------------------------------------------- tax (Phase 10 of docs/plans/ledger.md) */

/** A figure for each head of tax, as an exact decimal in the workspace's currency. */
export type TaxHeadFigures = { readonly cgst: string; readonly sgst: string; readonly utgst: string; readonly igst: string; readonly single: string };

/** GET /v1/money/tax-payment?month=YYYY-MM: what a Tax payment for the month suggests (step 10.7). */
export type TaxPaymentSuggestResponse = {
  readonly regime: 'in_gst' | 'single' | 'sales_tax';
  readonly month: string;
  readonly currency: string;
  readonly collected: TaxHeadFigures;
  readonly claimable: TaxHeadFigures;
  readonly setOff: readonly { readonly credit: keyof TaxHeadFigures; readonly against: keyof TaxHeadFigures; readonly amount: string }[];
  readonly creditUsed: TaxHeadFigures;
  readonly toPay: TaxHeadFigures;
  readonly carried: TaxHeadFigures;
  readonly total: string;
};

/** A period's bounds: `from` kept, `until` the first day after. */
export type MoneyPeriodBounds = { readonly from: string; readonly until: string };

/** GET /v1/money/profit?period=this_fy: income less expenses over a period of the financial year (10.2, L51). */
export type ProfitResponse = {
  readonly period: MoneyPeriodBounds;
  readonly currency: string;
  readonly income: string;
  readonly expenses: string;
  readonly profit: string;
  readonly toSort: { readonly amount: string; readonly entries: number };
};

/**
 * GET /v1/money/explainer: the Dashboard's How money works here card (step
 * 13.2 of docs/plans/ledger.md, L60), the four lines `explainMoney` says from
 * the workspace's live money records, financial year, tax and approvals.
 * `explainer` is null when no record moves money yet; `dismissed` is this
 * person's own (`POST /v1/money/explainer/dismiss`). `accountsRecordId` is
 * the Accounts list the card links to, null before the money kit made one.
 */
export type MoneyExplainerResponse = {
  readonly explainer: { readonly title: string; readonly lines: readonly [string, string, string, string] } | null;
  readonly dismissed: boolean;
  readonly accountsRecordId: string | null;
};

/** POST /v1/money/explainer/dismiss: the card put away for this person, on every device. */
export type MoneyExplainerDismissResponse = { readonly dismissed: true };

/** GET /v1/money/close-days: the days a close offers, read from the financial year (10.2). */
export type CloseDaysResponse = {
  readonly monthEnd: string;
  readonly yearEnd: string;
  readonly year: MoneyPeriodBounds;
  readonly closedThrough: string | null;
};

/** GET /v1/money/trial-balance?on=YYYY-MM-DD: the accountant view's trial balance (10.2, L32). Balances debit positive. */
export type TrialBalanceResponse = {
  readonly on: string;
  readonly year: MoneyPeriodBounds;
  readonly currency: string;
  readonly rows: readonly { readonly accountId: string; readonly entryId: string; readonly name: string; readonly type: string; readonly systemKey: string | null; readonly balance: string }[];
  readonly earlierYears: string;
  readonly debits: string;
  readonly credits: string;
};

/** GET /v1/money/tax-card?period=this_month: the tax card (10.8). Amounts exact decimals. */
export type TaxCardResponse = {
  readonly period: MoneyPeriodBounds;
  readonly currency: string;
  readonly regime: 'none' | 'in_gst' | 'single' | 'sales_tax' | null;
  readonly collected: string;
  readonly claimable: string;
  readonly net: string;
  readonly paid: string;
};

/** GET /v1/money/tax-summary?period=: the accountant view's tax summary, by rate, and under in_gst by buyer GSTIN and by HSN or SAC (10.8). */
export type TaxSummaryResponse = TaxCardResponse & {
  readonly byRate: readonly { readonly side: 'output' | 'input'; readonly ratePercent: string; readonly taxable: string; readonly tax: TaxHeadFigures; readonly total: string }[];
  readonly byParty: readonly { readonly taxId: string | null; readonly place: string | null; readonly ratePercent: string; readonly taxable: string; readonly tax: TaxHeadFigures; readonly total: string }[];
  readonly byCode: readonly { readonly hsn: string | null; readonly ratePercent: string; readonly taxable: string; readonly tax: TaxHeadFigures; readonly total: string }[];
};

/** GET /v1/money/export-pack?period=: the files the export pack holds for a period; each is GET /v1/money/export-pack/:name?period= as CSV. */
export type ExportPackResponse = {
  readonly period: MoneyPeriodBounds;
  readonly files: readonly { readonly name: string; readonly rows: number }[];
};

/* ------------------------------------------------- matches and the statement */
/*
 * Phase 4 of docs/plans/ledger.md: what a party owes, line by line, and which
 * payment paid which charge (L21). Amounts are exact decimal strings in the
 * line's unit, always at or above nought; which way a line moved is its
 * `kind`. On a receivable a charge raises what the party owes and a payment
 * lowers it; on a payable the same words read for what the business owes.
 */

/** One match seen from one side: on a charge, the payment that paid it; on a payment, the charge it paid. */
export type LineMatchView = {
  /** The match (data.line_match). PATCH /v1/line-matches/:id moves it. */
  id: string;
  /** The line on the other side. */
  lineId: string;
  entryId: string | null;
  recordId: string | null;
  /** The record's name, as the sidebar shows it: "Daily sales". */
  recordName: string | null;
  /** The entry's title, as its list shows it. */
  title: string | null;
  /** The other line's day, `YYYY-MM-DD`. */
  on: string;
  /** How much of the charge this payment pays. */
  amount: string;
  /** Null when paying the oldest charge first matched it; the person who moved it otherwise. */
  movedBy: string | null;
};

/** One line of a party's statement, oldest first. */
export type StatementLine = {
  lineId: string;
  entryId: string | null;
  recordId: string | null;
  recordName: string | null;
  title: string | null;
  on: string;
  /** `charge` raised what is owed (a sale, a fee); `payment` lowered it (a receipt, a concession, money in against the dues). */
  kind: 'charge' | 'payment';
  amount: string;
  /** What is owed after this line. Below nought: paid in advance (L25). */
  balance: string;
  /** A charge: what is still due of it. A payment: what of it pays no charge yet (an advance). */
  open: string;
  /** The payments that paid this charge, or the charges this payment paid. */
  matches: LineMatchView[];
  /** Written by a charge run (L23), so the page shows the Auto badge. */
  auto: boolean;
};

/** One month of a party's charges: paid, part paid or due. */
export type StatementPeriod = {
  /** `YYYY-MM`. */
  period: string;
  charged: string;
  paid: string;
  due: string;
  state: 'paid' | 'part' | 'due';
};

/**
 * GET /v1/records/:recordId/entries/:entryId/statement-lines — the statement of a
 * party (a row of a list, or an entry picked as the party, L46) on one
 * open-item account: the receivable unless `?account=<system_key>` names
 * another (`payable`). Needs the right to read the money (L27). Unlike
 * `PartyStatementResponse` (the row page's statement, every account at
 * once), it carries each match's id, so a person can move one (4.2), and the
 * months paid, part paid and due.
 */
export type PartyStatementLinesResponse = {
  partyEntryId: string;
  account: { id: string; systemKey: string | null; name: string | null };
  /** Null when nothing has posted for this party yet. */
  unit: string | null;
  lines: StatementLine[];
  /** Every charge's amount, added up: Billed. */
  charged: string;
  /** Every payment's amount, added up: Received. */
  paid: string;
  /** Charged less paid: Pending. Below nought is paid in advance. */
  balance: string;
  /** What is still due of the charges: "₹4,200 from 3 sales". */
  due: string;
  /** The charges with something still due. */
  openCharges: number;
  /** Payments that pay no charge yet. */
  advance: string;
  periods: StatementPeriod[];
};

/** One charge a payment paid, as the payment's Money effect names it (L26). */
export type PaidCharge = {
  matchId: string;
  lineId: string;
  entryId: string | null;
  recordId: string | null;
  recordName: string | null;
  title: string | null;
  /** The charge's day, `YYYY-MM-DD`. */
  on: string;
  /** `YYYY-MM`: "May is paid". */
  period: string;
  /** The charge's whole amount; null when its record is not open to the reader, or its amount is hidden (L27). */
  charged: string | null;
  /** What this payment paid of it. */
  paid: string;
  /** What is still due of it after every payment; null as `charged` is. */
  due: string | null;
  movedBy: string | null;
};

/**
 * The charges the lines of an entry paid, and what of it pays none yet (an
 * advance), each with its match, so a payment can be moved (4.2): read with
 * the entry's matches, GET …/entries/:entryId/matches (`EntryMatchesResponse`).
 * An entry that posted no payment answers no charges and no advance.
 */
export type EntryPaidChargesResponse = {
  unit: string | null;
  charges: PaidCharge[];
  /** What of this entry's payment pays no charge yet. Null when all of it does. */
  advance: string | null;
  /**
   * What this entry charged a party, and what of it no payment has paid yet:
   * the bill's own due, read from the ledger (D2, owner 31 Oct), never from a
   * Balance on its form. Absent or null for an entry that charged nobody.
   */
  owed?: { readonly charged: string; readonly due: string } | null;
};

/**
 * PATCH /v1/line-matches/:id — a person moves a payment to another charge:
 * the old match is voided and a new one written, from the same payment to
 * `debitLineId` (L21). The amount is the old match's, or `amount` when it
 * says less. A match never changes a balance.
 */
export type LineMatchMoveRequest = {
  /**
   * The charge the payment pays instead: a line of the same account, party
   * and unit (a debit on what is owed to the business, a credit on the
   * payable; the name is the receivable's).
   */
  debitLineId: string;
  amount?: string;
};

export type LineMatchMoveResponse = {
  /** The match that was voided. */
  voided: string;
  match: { id: string; debitLineId: string; creditLineId: string; amount: string; matchedOn: string };
};

/**
 * What a party owes on the receivable of a record's money rule, and where the
 * form keeps the answer (L24), as `duesOf` works it out. The route serves it
 * as `DuesAskResponse`, GET /v1/records/:recordId/dues.
 */
export type PartyDuesResponse = {
  ask: boolean;
  /** The hidden yes-or-no field the answer is kept in. Null when the rule keeps none. */
  againstDuesFieldId: string | null;
  /** The field the rule reads the party from. Null when the rule reads none. */
  partyFieldId: string | null;
  partyEntryId: string;
  /** What the party owes now, above nought; "0" when nothing. */
  dues: string;
  /** How many charges it is from: "from 3 sales". */
  openCharges: number;
  unit: string | null;
};

/**
 * One row of a party's statement (4.3 of docs/plans/ledger.md): one line of a
 * live posting on an account whose lines get matched, for this party. A line a
 * reversal undid is not a row: an edited receipt shows once, at what it says
 * now. Figures are exact decimals, never negative.
 */
export type StatementRow = {
  readonly lineId: string;
  /** `YYYY-MM-DD`: the day the line posts on. */
  readonly on: string;
  /** The entry that posted it, and its record, to open it. Null for a line no entry wrote. */
  readonly entryId: string | null;
  readonly recordId: string | null;
  readonly recordName: string | null;
  /** What this row adds to what is owed (a charge, a sale, a bill), or null. */
  readonly charged: string | null;
  /** What this row takes off it (a payment, a concession), or null. */
  readonly paid: string | null;
  /** What is owed after this row, in the account's own sense: below nought is paid in advance (L25). */
  readonly balance: string;
  /**
   * The rows on the other side that this one is matched to (L21): for a
   * charge, the payments that paid it; for a payment, the charges it paid.
   * Oldest first. Each `amount` is how much of the charge that payment paid.
   */
  readonly matches: readonly { readonly lineId: string; readonly on: string; readonly amount: string }[];
};

/** One account's statement for a party: Fees receivable, or what you owe a supplier. */
export type StatementAccount = {
  readonly accountId: string;
  /** The account's name: its row in the Accounts list. */
  readonly name: string;
  /** `owed_to_you`: an asset such as the receivable. `you_owe`: a liability such as the payable. */
  readonly side: 'owed_to_you' | 'you_owe';
  readonly unit: string;
  /** Every charge, every payment, and what is owed now (the last row's balance). */
  readonly billed: string;
  readonly received: string;
  readonly balance: string;
  /** Oldest first, with the running balance. */
  readonly rows: readonly StatementRow[];
};

/**
 * GET /v1/records/:recordId/entries/:entryId/statement — the statement of the
 * party that row of a list is (4.3, L32), or that an entry is when its
 * record's rule makes each entry its own party (L46, `moneySelfParty`):
 * charges, payments, the running balance, and which charges are paid and
 * which are due. Needs the right to read the money (L27) and to open the
 * entry. `accounts` is empty for a row nothing was posted to.
 */
export type PartyStatementResponse = {
  readonly partyEntryId: string;
  readonly accounts: readonly StatementAccount[];
};

/**
 * One match an entry is part of (4.4): `pays` when this entry's payment paid
 * that charge, `paid_by` when this entry's charge was paid by that payment.
 */
export type EntryMatch = {
  readonly side: 'pays' | 'paid_by';
  /** How much of the charge the payment paid, an exact decimal. */
  readonly amount: string;
  readonly unit: string;
  /** The other entry: its day, and where to open it. */
  readonly on: string;
  readonly entryId: string | null;
  readonly recordId: string | null;
  readonly recordName: string | null;
};

/**
 * GET /v1/records/:recordId/entries/:entryId/matches — the charges a receipt
 * paid, or the payments that paid a charge (4.4, L26), oldest first. The entry's
 * Money effect names them. Empty when nothing is matched.
 */
export type EntryMatchesResponse = { readonly matches: readonly EntryMatch[] } & EntryPaidChargesResponse;

/**
 * GET /v1/records/:recordId/dues?party=<entryId> — whether this record's
 * form asks "Is this against the dues?" (L24, 4.7), and the party's dues:
 * the one read of the dues, for the form and for OS (step 12.4).
 *
 * `ask` names the two fields the question reads and writes: the party picked,
 * and the hidden yes-or-no field that keeps the answer. Null when the record's
 * money rule has no such question. `dues` is what the party owes now, above
 * nought, or null when it owes nothing or no party was named.
 */
export type DuesAskResponse = {
  readonly ask: { readonly partyFieldId: string; readonly flagFieldId: string } | null;
  readonly dues: {
    readonly amount: string;
    readonly unit: string;
    readonly partyName: string;
    /** How many charges it is from: "from 3 sales". */
    readonly openCharges: number;
  } | null;
};

/* ------------------------------------------------- pay from the statement */

/** One open bill a payment line can go on: a charge of the party still due, on a record whose bills take payment lines. */
export type PayBill = {
  readonly recordId: string;
  readonly recordName: string;
  readonly entryId: string;
  /** `YYYY-MM-DD`: the day the charge posts on. */
  readonly on: string;
  /** What is still due of it, an exact decimal above nought. */
  readonly due: string;
};

/**
 * One way to settle a party's account on one account:
 *
 *   record  a record of payments that picks the party (Buyer payments,
 *           Udhaar wapsi): its new-entry form opens with the party and the
 *           amount filled, and the payment pays the oldest charges (L21)
 *   lines   the bills pay on themselves (M2: Doodh supply's Later payments):
 *           one payment is spread over the open bills, oldest first, a
 *           payment line on each (POST …/pay)
 */
export type PayWay =
  | {
      readonly kind: 'record';
      readonly recordId: string;
      readonly recordName: string;
      readonly partyFieldId: string;
      readonly amountFieldId: string;
      /** The record's day field, filled with today. Null when it binds none. */
      readonly dateFieldId: string | null;
    }
  | {
      readonly kind: 'lines';
      /** The ways a line says it was paid, from the lines' own mode field. Empty when the lines name none. */
      readonly modes: readonly { readonly id: string; readonly label: string }[];
      /** Oldest first. */
      readonly bills: readonly PayBill[];
    };

export type PayAccount = {
  readonly accountId: string;
  /** The account's name: its row in the Accounts list. */
  readonly name: string;
  /** `in`: the party owes the business (Payment received). `out`: the business owes the party (Payment made). */
  readonly direction: 'in' | 'out';
  readonly unit: string;
  /** What is owed now on this account, an exact decimal; nought or below means nothing is due. */
  readonly due: string;
  /** Empty when nothing this person may write pays it. */
  readonly ways: readonly PayWay[];
};

/**
 * GET /v1/records/:listId/entries/:entryId/pay-ways — how this person may
 * settle the account of the party that row of a list is, account by account.
 * Needs the right to read the money (L27) and to open the row; a way is named
 * only where this person may write it.
 */
export type PayWaysResponse = {
  readonly partyEntryId: string;
  readonly accounts: readonly PayAccount[];
};

/**
 * POST /v1/records/:listId/entries/:entryId/pay — one payment spread over the
 * party's open bills on one account, oldest first: a payment line on each, in
 * one transaction. Refused whole when it is more than the bills owe, or when
 * one bill may not take it.
 */
export type PayRequest = {
  readonly accountId: string;
  /** An exact decimal above nought. */
  readonly amount: string;
  /** `YYYY-MM-DD`: the day it was paid. */
  readonly on: string;
  /** The option id of the lines' mode field, or null when the lines name none. */
  readonly mode: string | null;
};

export type PayResponse = {
  /** Each bill that took a line, oldest first, with what it took. */
  readonly paid: readonly { readonly recordId: string; readonly entryId: string; readonly amount: string }[];
  /** True when an approval chain holds one of the edits: it counts once approved. */
  readonly waits: boolean;
};

/* ------------------------------------------------------- party periods (L22) */

/**
 * One stretch of days a party (a student, a member) is active: from the day
 * its `joined_on` field says, to the day its `left_on` field says, or open
 * while it has not left. Saving the party row writes or closes it; a party
 * who rejoins gets a new one. Two of one party never overlap.
 */
export type PartyPeriod = {
  id: string;
  /** `YYYY-MM-DD`, the first active day; `PARTY_ACTIVE_SINCE_ALWAYS` when the row has no joining day. */
  activeFrom: string;
  /** `YYYY-MM-DD`, the last active day; null while the party is still active. */
  activeTo: string | null;
};

/**
 * The `activeFrom` of a period whose party row has a leaving day but no
 * joining day: active since always, the way a row with no period is, up to
 * the day it left. A joining day typed later replaces it.
 */
export const PARTY_ACTIVE_SINCE_ALWAYS = '0001-01-01';

/** GET /v1/records/:recordId/entries/:entryId/periods — a party row's periods, oldest first. */
export type PartyPeriodsResponse = { periods: PartyPeriod[] };

/* ---------------------------------------------------------- charge runs (L23) */

/** How often a charge plan bills: each month, each quarter or each financial year. */
export type ChargeCadence = 'monthly' | 'quarterly' | 'yearly';

/** `skipped`: a person chose "Skip this period". `failed`: some charges could not be written; the next run tries again. */
export type ChargeRunStatus = 'pending' | 'done' | 'failed' | 'skipped';

/** One run of a charge plan, as the Charges record lists it under "Earlier runs". */
export type ChargeRunItem = {
  id: string;
  /** `YYYY-MM-DD`: the first day the run billed. */
  periodStart: string;
  /** `YYYY-MM-DD`: the last day the run billed. */
  periodEnd: string;
  status: ChargeRunStatus;
  /** How many parties it charged. */
  parties: number;
  /** What it charged in all, an exact decimal in `currency`. */
  total: string;
  currency: string;
  /** ISO 8601. */
  startedAt: string;
  finishedAt: string | null;
  /** How many of its charges a person changed or deleted since: "1 charge changed by hand". */
  changedByHand: number;
};

/** The run that comes next, for the strip above the Charges record's entries and the reminder the day before. */
export type ChargeRunNext = {
  periodStart: string;
  periodEnd: string;
  /** `YYYY-MM-DD`: the day the run bills on its own, the period's first day. */
  runOn: string;
  /** The parties it would charge, as the plan and the party periods stand today. */
  parties: number;
  /** What it would charge in all, an exact decimal in the record's currency. */
  total: string;
};

/**
 * GET /v1/records/:recordId/charge-runs — on the Charges record: its plan,
 * the next run and the earlier runs, newest first. `plan` is null on a record
 * no charge plan writes into; then `next` is null and `runs` empty.
 */
export type ChargeRunsResponse = {
  plan: {
    ruleId: string;
    /** The party list the plan sits on (Students). */
    partyRecordId: string;
    partyRecordName: string;
    /** The party list's word for one of its rows (`noun.student`), so a page can say "3 students"; '' when it has none. */
    partyEntryNounKey: string;
    cadence: ChargeCadence;
    currency: string;
    /**
     * Who paused the plan and on which day (R7 of docs/plans/routines.md):
     * a paused plan bills nothing and reminds no one. Null while it runs. The
     * server always answers it; optional for the fixtures.
     */
    paused?: RoutinePaused | null;
  } | null;
  next: ChargeRunNext | null;
  runs: ChargeRunItem[];
};

/**
 * POST /v1/records/:recordId/charge-runs/run ("Run now") and
 * POST /v1/records/:recordId/charge-runs/skip ("Skip this period"): both act
 * on the next run, named by its period start, so a page drawn yesterday never
 * runs or skips a period it did not show. A stale one is refused with
 * CHARGE_RUN_MOVED (409).
 */
export type ChargeRunActionRequest = { periodStart: string };

/** What Run now or Skip did: the run, and how many charges this call wrote (0 for a skip). */
export type ChargeRunActionResponse = { run: ChargeRunItem; written: number };

/** A charge plan's next run, for the Dashboard's reminder the day before (5.4). */
export type UpcomingChargeRun = ChargeRunNext & {
  ruleId: string;
  cadence: ChargeCadence;
  /** The Charges record the run writes into. */
  recordId: string;
  recordName: string;
  /** The party list it bills, and its word for one row; '' when it has none. */
  partyRecordId: string;
  partyRecordName: string;
  partyEntryNounKey: string;
  currency: string;
};

/**
 * GET /v1/charge-runs/upcoming — the next run of each charge plan whose
 * Charges record the reader may run: owners and admins, or anyone with
 * add_entry on it. The web keeps those that run tomorrow.
 */
export type UpcomingChargeRunsResponse = { upcoming: UpcomingChargeRun[] };

/* ------------------------------------------------ revenue per active party (5.5) */

/** One month of revenue per active party. */
export type RevenuePerPartyMonth = {
  /** `YYYY-MM`. */
  month: string;
  /** Income less discounts given, from entries that name a party of the list, an exact decimal. */
  revenue: string;
  /** Parties active on any day of the month. */
  parties: number;
  /** `revenue / parties`, rounded to the currency's digits; null when no party was active. */
  perParty: string | null;
};

/**
 * GET /v1/ledger/revenue-per-party?recordId=<party list>&from=YYYY-MM&to=YYYY-MM
 * — the read a "Revenue per student" card answers from (5.5 of
 * docs/plans/ledger.md). Owners and admins only, until the books right lands.
 */
export type RevenuePerPartyResponse = {
  partyRecordId: string;
  currency: string;
  months: RevenuePerPartyMonth[];
};

/* ------------------------------------- the close and the accountant view (Phase 6 of docs/plans/ledger.md) */

/**
 * GET /v1/money/close-check?through=YYYY-MM-DD: what stands in the way of
 * closing every day on or before `through` (step 6.1, L12, L17): the money
 * waiting in To sort and the entries not yet approved. Owners and admins
 * (the `close_period` capability).
 */
export type CloseCheckResponse = {
  readonly through: string;
  /** The last day already closed, or null. */
  readonly closedThrough: string | null;
  readonly currency: string;
  /** Entries whose money waits in To sort on or before `through`, and how much (an exact decimal, debit positive). */
  readonly toSort: { readonly entries: number; readonly amount: string };
  /** Entries dated on or before `through` that are drafts or wait for approval. */
  readonly waiting: { readonly drafts: number; readonly submitted: number };
  /** True when nothing stands in the way. */
  readonly ready: boolean;
};

/** POST /v1/money/close: close every day on or before `through`. */
export type CloseRequest = { readonly through: string };

/** What a close answers: the last closed day now. */
export type CloseResponse = { readonly closedThrough: string };

/**
 * The refusals of a close, 409 unless named:
 *   CLOSE_TO_SORT          To sort holds money on or before the day (L17); `check` names the count
 *   CLOSE_ENTRIES_WAITING  drafts or submitted entries are dated inside it
 *   CLOSE_ALREADY_CLOSED   the day is on or before the last closed day
 *   CLOSE_DAY_INVALID      400: not a day, or not yet passed
 *   CLOSE_NOT_ALLOWED      403: only owners and admins close
 */
export const CLOSE_REFUSALS = ['CLOSE_TO_SORT', 'CLOSE_ENTRIES_WAITING', 'CLOSE_ALREADY_CLOSED', 'CLOSE_DAY_INVALID', 'CLOSE_NOT_ALLOWED'] as const;
export type CloseRefusal = (typeof CLOSE_REFUSALS)[number];

/** The 409 body a blocked close answers with. */
export type CloseRefusedResponse = {
  readonly error: { readonly code: 'CLOSE_TO_SORT' | 'CLOSE_ENTRIES_WAITING'; readonly message: string; readonly check: CloseCheckResponse };
};

/** The refusals of a correction (L12): see `EntryCorrectRequest`. */
export const CORRECT_REFUSALS = ['ENTRY_NOT_CLOSED', 'ENTRY_ALREADY_CORRECTED', 'ENTRY_IS_CORRECTION'] as const;
export type CorrectRefusal = (typeof CORRECT_REFUSALS)[number];

/** One account on the balance sheet, on its natural side: an amount positive when it holds what its side holds. */
export type BalanceSheetRow = {
  readonly accountId: string;
  readonly entryId: string;
  readonly name: string;
  readonly type: string;
  readonly systemKey: string | null;
  readonly amount: string;
  /**
   * True for the parties of an open-item account who stand on the other side
   * (L25): customers who paid in advance sit with what is owed, and advances
   * to suppliers with what is held. Never netted with the account's own row.
   */
  readonly advance: boolean;
};

/**
 * GET /v1/money/balance-sheet?on=YYYY-MM-DD: the accountant view's balance
 * sheet (step 6.2, L32), beside the trial balance, read over the financial
 * year. Amounts are exact decimals on their side's natural sign; the owner's
 * side carries this year's profit so far and the earlier years' as two lines,
 * and `assets` equals `liabilities` plus `equity`.
 */
export type BalanceSheetResponse = {
  readonly on: string;
  readonly year: MoneyPeriodBounds;
  readonly currency: string;
  readonly assets: readonly BalanceSheetRow[];
  readonly liabilities: readonly BalanceSheetRow[];
  readonly equity: readonly BalanceSheetRow[];
  /** Income less expenses from the year's start through `on`. */
  readonly profitThisYear: string;
  /** The profit of the years before, kept by the owner. */
  readonly earlierYears: string;
  readonly totals: { readonly assets: string; readonly liabilities: string; readonly equity: string };
};

/** One income or expense account over a period: its amount on its natural side (income credit, expense debit). */
export type ProfitAndLossRow = { readonly accountId: string; readonly entryId: string; readonly name: string; readonly systemKey: string | null; readonly contra: boolean; readonly amount: string };

/**
 * GET /v1/money/profit-and-loss?period=: the accountant view's profit and
 * loss (step 6.2), account by account; its totals are /v1/money/profit's.
 */
export type ProfitAndLossResponse = {
  readonly period: MoneyPeriodBounds;
  readonly currency: string;
  readonly income: readonly ProfitAndLossRow[];
  readonly expenses: readonly ProfitAndLossRow[];
  readonly totalIncome: string;
  readonly totalExpenses: string;
  readonly profit: string;
};
